import { c as _c } from "react/compiler-runtime";
import { useSelectedVehicleDetails } from "@catchy/app-core/src/useSelectedVehicleDetails";
import { DialogAction } from "@catchy/ui";
import { MenuItem } from "@headlessui/react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { a } from "@react-spring/web";
import { GeolocateControl, Map as MapLibreMap } from "maplibre-gl";
import useSWR from "swr";
import checkStopType from "@/lib/stopTypeChecker";
import { getStopTypeDetails, wellingtonRouteDisplayOptions } from "@/lib/transitDisplay";
import { getTransportLabel, getDisplayPlatform, getStopCodeDisplay, buildStopIconExpression, buildScooterFeatureCollection, searchLocationIconSizeExpression, emptyScooterFeatureCollection, formatMicromobilityStationName, formatUpdatedLabel, getBaseMapQueryFrom, getClickedMapFeature, getFeatureByVehicleId, getMicromobilityAccentColor, getMicromobilityPluralNoun, getMicromobilityTitle, getRoundedReverseGeocodeCoordinates, getSelectedVehicleTripHref, withStoredStopId, getUsefulReverseGeocodeName, liveMapSelectionConfigs, micromobilityGroupDisplayRadiusMeters, micromobilityMinVisibleZoom, ensureMicromobilityMarkerImages, ensureCoreMapImages, coreMapImageIds, ensureVehicleRouteBadgeImages, createMainMapCameraController, createMapRecenterTarget, getMainMapInitialView, getMainMapLayout, getMainMapPadding, getMainMapUrlSelection, isBusLikeRouteType, normalizeCoordinate, shouldShowDoubleDeckerIcon, shouldShowElectricTransportIcon, stopHoverOpacityExpression, getVehicleNextStop, hasAffirmativelyUnknownVehicleStop, getPreferredStopName, getStoredStopId, hasFavouriteStopId, useTrackRecentStop, useRecentStopSummaries, useMainMapRuntime, useOptimisticMapQuery, useStopIcons, scooterHitLayerId, scooterLayerId, selectedScooterLayerId, pointerLayerId, selectedVehicleLayerId, canShowGeolocationControls, createNativeLocationPermissionRequest, createCatchyMap, getMaplibreStyle, mainMapMaxZoom, vehicleLayerId } from "@catchy/app-core";
import { getCanonicalSiteUrl, getMapCenterDefault, getMapOriginHref, getMapPageMetadata, getMapStopLayerIds, getMapStopSourceConfig, getStorageKey } from "@/lib/regions/client";
import useLocalStorage from "@/hooks/useLocalStorage";
import { hasNativeTwaMessagePort, nativeLocationPermissionAuthorizedEvent, nativeLocationPermissionRequestEvent, nativeLocationPermissionResultEvent, requestNativeLocationPermission } from "@/lib/nativeStorageBridge";
import { school_bus_schools } from "@/data/school_bus_schools";
import { BackLink, FavouriteButton, getDrawerBackdropProgress, IconButton, Modal, OptionsMenu, useMobileDrawer } from "@catchy/ui";
const NEW_ZEALAND_MAP_BOUNDS = [[164.5, -48.8], [179.9, -32.5]];
import SearchGeoCode from "@/components/SearchGeoCode";
import Recents from "components/Recents";
import StopRow from "@/components/StopRow";
import RouteBadge from "@/components/RouteBadge";
import { createRouteBadgeSvg } from "@/lib/routeBadgeSvg";
import Bus from "@catchy/assets/svgs/live-vehicles-bus.svg";
import Battery from "@catchy/assets/svgs/battery.svg";
import Chevron from "@catchy/assets/svgs/chevron-up.svg";
import Clock from "@catchy/assets/svgs/clock.svg";
import CloseCross from "@catchy/assets/svgs/close-cross.svg";
import Compass from "@catchy/assets/svgs/compass.svg";
import Ebike from "@catchy/assets/svgs/ebike.svg";
import Escooter from "@catchy/assets/svgs/escooter.svg";
import Layers from "@catchy/assets/svgs/layers.svg";
import LocationMono from "@catchy/assets/svgs/location-mono.svg";
import Micromobility from "@catchy/assets/svgs/micromobility.svg";
import MyLocation from "@catchy/assets/svgs/my-location.svg";
import Nearby from "@catchy/assets/svgs/nearby.svg";
import Spinner from "@catchy/assets/svgs/spinner.svg";
import StopMarker from "@catchy/assets/svgs/stop-mono-lg.svg";
import StopMarkerOutline from "@catchy/assets/svgs/stop-mono-lg-outline.svg";
import VehicleIcon from "@catchy/assets/svgs/vehicle-icon.svg";
import BusElectric from "@catchy/assets/svgs/bus-electric.svg";
import BusDoubleDecker from "@catchy/assets/svgs/bus-double-decker.svg";
import CrushedIcon from "@catchy/assets/svgs/occupancy/occupancy-crushed.svg";
import EmptyIcon from "@catchy/assets/svgs/occupancy/occupancy-empty.svg";
import FewSeatsIcon from "@catchy/assets/svgs/occupancy/occupancy-few-seats.svg";
import FullIcon from "@catchy/assets/svgs/occupancy/occupancy-full.svg";
import SeatsAvailableIcon from "@catchy/assets/svgs/occupancy/occupancy-seats-available.svg";
import StandingIcon from "@catchy/assets/svgs/occupancy/occupancy-standing.svg";
const fetcher = url => fetch(url).then(res => res.json());
const emptyVehicleFeatureCollection = {
  type: "FeatureCollection",
  features: []
};
const defaultVehicleStaleAfterMs = 90000;
const relativeMinuteFormatter = new Intl.RelativeTimeFormat("en-AU", {
  numeric: "always",
  style: "short"
});
const formatRelativeMinutes = seconds => relativeMinuteFormatter.format(Math.round(seconds / 60), "minute").replace(/^in\s+/i, "");
function getStopMarkerClassName(stopType) {
  if (stopType === "train") return "text-green-500";
  if (stopType === "cable_car") return "text-red-500";
  if (stopType === "ferry") return "text-blue-500";
  return "text-orange-400";
}
const mapCentreDefault = getMapCenterDefault();
const canonicalSiteUrl = getCanonicalSiteUrl();
const mapPageMetadata = getMapPageMetadata();
const {
  stops: mapStopsLayerId,
  activeStops: mapActiveStopsLayerId
} = getMapStopLayerIds();
const mapStopSourceConfig = getMapStopSourceConfig();
const defaultPanelWidth = 420;
const defaultMobileHeaderHeight = 124;
const mobileBodyClosedMinHeight = 56;
const mobileBodyOpenMinHeight = 184;
const mobileBodyOpenMaxHeight = 360;
const mobileBodyOpenMaxOffset = 280;
const mobileBodyOpenScreenFraction = 0.34;
const mobileDrawerFullTopGap = 10;
const defaultViewportHeight = 844;
const androidLocationPermissionSessionKey = "catchy_android_location_permission";
const getMobileBodyMaxHeight = (viewportHeight = defaultViewportHeight) => Math.max(mobileBodyOpenMinHeight, Math.min(mobileBodyOpenMaxHeight, viewportHeight - mobileBodyOpenMaxOffset, Math.round(viewportHeight * mobileBodyOpenScreenFraction)));
const getInitialMobileOpenHeight = (viewportHeight = defaultViewportHeight) => {
  return defaultMobileHeaderHeight + getMobileBodyMaxHeight(viewportHeight);
};
const getInitialMobileClosedHeight = () => defaultMobileHeaderHeight + mobileBodyClosedMinHeight;
const getInitialMobileFullHeight = (viewportHeight = defaultViewportHeight) => Math.max(getInitialMobileOpenHeight(), viewportHeight - mobileDrawerFullTopGap);
const formatDistance = distance => distance < 1000 ? `${Math.round(distance)}m` : `${(distance / 1000).toFixed(1)}km`;
const formatDroppedPinName = (lng, lat) => `Dropped pin ${lat.toFixed(5)}, ${lng.toFixed(5)}`;
const buildVehicleFeatureCollection = vehicleRecords => ({
  type: "FeatureCollection",
  features: Object.values(vehicleRecords).map(vehicle => ({
    type: "Feature",
    properties: {
      id: vehicle.tripId,
      tripId: vehicle.tripId,
      startDate: vehicle.startDate,
      routeId: vehicle.routeId,
      routeBadgeId: vehicle.routeBadgeId,
      routeImageId: `route-wellington-${vehicle.routeBadgeId}`,
      routeLabel: vehicle.routeLabel,
      routeColor: vehicle.routeColor,
      routeType: vehicle.routeType,
      vehicleId: vehicle.vehicleId,
      occupancyStatus: vehicle.occupancyStatus,
      bearing: vehicle.bearing,
      directionId: vehicle.directionId
    },
    geometry: {
      type: "Point",
      coordinates: [vehicle.longitude, vehicle.latitude]
    }
  }))
});
const getQueryValue = value => Array.isArray(value) ? value[0] : value;
const getQueryNumber = value => {
  const parsed = parseFloat(getQueryValue(value));
  return Number.isFinite(parsed) ? parsed : null;
};
const getMapHistoryState = query => ({
  stop: getQueryValue(query.stop) || null,
  vehicle: getQueryValue(query.vehicle) || null,
  scooter: getQueryValue(query.scooter) || null,
  pin: getQueryValue(query.pin) || null,
  lat: getQueryNumber(query.lat),
  lng: getQueryNumber(query.lng)
});
const isPlainMapHistoryState = state => state.stop === null && state.vehicle === null && state.scooter === null && state.pin === null;
const isPinOnlyHistoryState = state => state.pin === "1" && state.stop === null && state.vehicle === null && state.scooter === null;
const getOccupancyDetails = status => {
  switch (status) {
    case "EMPTY":
      return {
        description: "Empty",
        colorClass: "text-green-600 dark:text-green-400 opacity-50",
        IconComponent: EmptyIcon
      };
    case "MANY_SEATS_AVAILABLE":
      return {
        description: "Seats available",
        colorClass: "text-green-600 dark:text-green-400",
        IconComponent: SeatsAvailableIcon
      };
    case "FEW_SEATS_AVAILABLE":
      return {
        description: "Few seats available",
        colorClass: "text-amber-400 dark:text-amber-300",
        IconComponent: FewSeatsIcon
      };
    case "STANDING_ROOM_ONLY":
      return {
        description: "Standing room only",
        colorClass: "text-orange-400",
        IconComponent: StandingIcon
      };
    case "CRUSHED_STANDING_ROOM_ONLY":
      return {
        description: "Very limited standing room",
        colorClass: "text-rose-600/80 dark:text-rose-500/80",
        IconComponent: CrushedIcon
      };
    case "FULL":
      return {
        description: "Full. May not be accepting passengers",
        colorClass: "text-rose-600/80 dark:text-rose-500/80",
        IconComponent: FullIcon
      };
    case "NOT_ACCEPTING_PASSENGERS":
      return {
        description: "Not accepting passengers.",
        colorClass: "text-rose-600/80 dark:text-rose-500/80",
        IconComponent: FullIcon
      };
    case "NOT_BOARDABLE":
      return {
        description: "Not boardable.",
        colorClass: "text-rose-600/80 dark:text-rose-500/80",
        IconComponent: FullIcon
      };
    case "NO_DATA_AVAILABLE":
      return {
        description: "No data available.",
        colorClass: "text-zinc-400/60",
        IconComponent: FullIcon
      };
    default:
      return null;
  }
};
const shouldShowVehiclePanelOccupancy = routeType => {
  const normalizedRouteType = Number(routeType);
  return normalizedRouteType !== 2 && normalizedRouteType !== 4;
};
const shouldLookupVehicleMetadata = routeType => {
  const normalizedRouteType = Number(routeType);
  return isBusLikeRouteType(normalizedRouteType) || normalizedRouteType === 2 || normalizedRouteType === 4 || normalizedRouteType >= 100 && normalizedRouteType <= 199;
};
const formatNzDateKey = dateValue => new Date(dateValue).toLocaleString("en-CA", {
  timeZone: "Pacific/Auckland",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});
const MapStopDepartureRow = t0 => {
  const $ = _c(41);
  const {
    departure,
    route,
    stopId
  } = t0;
  const departureTime = departure.expected_departure_time || departure.scheduled_departure_time;
  const secondsUntilDeparture = departureTime ? (Date.parse(departureTime) - Date.now()) / 1000 : Number.NaN;
  const isTripCancelled = Boolean(departure.cancelled && !departure.expected_departure_time);
  let t1;
  if ($[0] !== departure.scheduled_departure_time) {
    t1 = departure.scheduled_departure_time ? formatNzDateKey(departure.scheduled_departure_time) : "";
    $[0] = departure.scheduled_departure_time;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const datePart = t1;
  const tripHref = departure.trip_id ? `/trip/${encodeURIComponent(departure.trip_id)}?stop_id=${stopId}&date=${encodeURIComponent(datePart)}` : `/trip/trip-unavailable?stop_id=${stopId}`;
  let t2;
  if ($[2] !== departure.platform || $[3] !== departureTime || $[4] !== stopId) {
    t2 = getDisplayPlatform(stopId, departure.platform, departureTime);
    $[2] = departure.platform;
    $[3] = departureTime;
    $[4] = stopId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const displayPlatform = t2;
  const t3 = route?.route_color;
  const t4 = route?.route_text_color;
  const t5 = departure.route_short_name || route?.route_short_name;
  const t6 = route?.route_type;
  let t7;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = <RouteBadge route_color={t3} route_text_color={t4} routeLabel={t5} route_type={t6} className="mr-2 h-9 w-9 shrink-0" />;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = departure.stop_headsign || departure.trip_headsign;
  let t9;
  if ($[11] !== t8) {
    t9 = <h3 className="break-words">{t8}</h3>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== departure.route_short_name || $[14] !== departure.trip_headsign || $[15] !== route?.route_short_name) {
    t10 = departure.trip_headsign === "School Bus" && <h2 className="pt-1 text-sm leading-none opacity-60">{school_bus_schools.get(departure.route_short_name || route?.route_short_name)}</h2>;
    $[13] = departure.route_short_name;
    $[14] = departure.trip_headsign;
    $[15] = route?.route_short_name;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <div className="flex-1 leading-none">{t9}{t10}</div>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== departure.expected_departure_time || $[21] !== departure.scheduled_departure_time || $[22] !== isTripCancelled || $[23] !== secondsUntilDeparture) {
    t12 = departure.expected_departure_time && Math.round(secondsUntilDeparture) < 3600 ? secondsUntilDeparture < 70 ? <span className="font-bold">Due</span> : <><span>{formatRelativeMinutes(secondsUntilDeparture)}</span><span className="text-sm leading-none opacity-60"><br />{new Date(departure.expected_departure_time).toLocaleTimeString("en-AU", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
          timeZone: "Pacific/Auckland"
        })}</span></> : <span className={isTripCancelled ? "pt-1 text-sm leading-none opacity-60" : ""}>{departure.scheduled_departure_time ? new Date(departure.scheduled_departure_time).toLocaleTimeString("en-AU", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZone: "Pacific/Auckland"
      }) : ""}</span>;
    $[20] = departure.expected_departure_time;
    $[21] = departure.scheduled_departure_time;
    $[22] = isTripCancelled;
    $[23] = secondsUntilDeparture;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== isTripCancelled) {
    t13 = isTripCancelled && <span className="font-bold leading-none text-rose-600 dark:text-rose-500"><br />Cancelled</span>;
    $[25] = isTripCancelled;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== displayPlatform || $[28] !== isTripCancelled) {
    t14 = displayPlatform && !isTripCancelled && <span className="block pt-1 text-sm leading-none opacity-60">{`Platform ${displayPlatform}`}</span>;
    $[27] = displayPlatform;
    $[28] = isTripCancelled;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t13 || $[32] !== t14) {
    t15 = <div className=" shrink w-[clamp(2rem,5.5rem,5.5rem)] text-right tabular-nums leading-none">{t12}{t13}{t14}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t11 || $[35] !== t15 || $[36] !== t7) {
    t16 = <div className="flex min-h-[1px] items-center gap-x-1 py-3 text-lg">{t7}{t11}{t15}</div>;
    $[34] = t11;
    $[35] = t15;
    $[36] = t7;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16 || $[39] !== tripHref) {
    t17 = <Link href={tripHref} className="transition-colors block min-h-[1px] px-[20px] outline-none ring-blue-500/50 duration-150 hover:bg-zinc-400/10 focus-visible:ring focus-visible:ring-inset active:bg-zinc-400/10">{t16}</Link>;
    $[38] = t16;
    $[39] = tripHref;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
};
const transportTranslator = (type, identifier, vehicle) => {
  const indicators = [shouldShowElectricTransportIcon(type, identifier, vehicle) ? <BusElectric className="size-4" /> : null, shouldShowDoubleDeckerIcon(vehicle) ? <BusDoubleDecker className="size-4" /> : null].filter(Boolean);
  return <>
      <div className="relative mr-3 grid h-7 w-9 shrink-0 place-items-center">
        <div className="flex items-center justify-center">
          {indicators.map((indicator, index) => <div key={index} className={index === 0 ? "" : "-ml-1"}>
              {indicator}
            </div>)}
        </div>
      </div>
      <div className="min-w-0 pt-0.5">
        <div className="leading-tight">
          {getTransportLabel(type, identifier, vehicle)}
        </div>
      </div>
    </>;
};
const Map = ({
  isDark,
  preferredStopNames,
  preferSatellite,
  setPreferredStopNames,
  setPreferSatellite
}) => {
  const router = useRouter();
  const {
    query
  } = useRouter();
  const {
    queryRef,
    updateMapQuery
  } = useOptimisticMapQuery({
    router,
    query
  });
  const initialMapSelectionRef = useRef(getMainMapUrlSelection(query, typeof window === "undefined" ? "" : window.location.search));
  const initialMapSelection = initialMapSelectionRef.current;
  const mapContainerRef = useRef();
  const mobileChromeRef = useRef();
  const mobileMapRef = useRef();
  const mobileInlineRecentreRef = useRef();
  const panelRef = useRef();
  const desktopPanelBodyRef = useRef();
  const mobileSheetHeaderRef = useRef();
  const mobileSheetBodyRef = useRef();
  const mobileSheetBodyContentRef = useRef();
  const map = useRef();
  const pendingVehicleBadgeImageIdsRef = useRef(new Set());
  const vehiclePositionsRef = useRef(emptyVehicleFeatureCollection);
  const scooterPositionsRef = useRef(emptyScooterFeatureCollection);
  const [favourites, setFavourites] = useLocalStorage(getStorageKey("favourites"), []);
  const [recents, setRecents] = useLocalStorage(getStorageKey("recents"), []);
  const [height, setHeight] = useState(getInitialMobileOpenHeight);
  const [closeHeight, setCloseHeight] = useState(getInitialMobileClosedHeight);
  const [fullHeight, setFullHeight] = useState(getInitialMobileFullHeight);
  const [viewport, setViewport] = useState({
    width: 0,
    height: 0
  });
  const [panelWidth, setPanelWidth] = useState(defaultPanelWidth);
  const [panelScrolled, setPanelScrolled] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [panelMode, setPanelMode] = useState("selection");
  const [mobileSearchActive, setMobileSearchActive] = useState(false);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [renameModalIsOpen, setRenameModalIsOpen] = useState(false);
  const [preferredStopNameTemp, setPreferredStopNameTemp] = useState("");
  const renameInputRef = useRef(null);
  const viewportHeight = viewport.height || defaultViewportHeight;
  const mobileBodyMaxHeight = getMobileBodyMaxHeight(viewportHeight);
  const isDesktop = getMainMapLayout({
    width: viewport.width,
    height: viewportHeight
  }).isDesktop;
  const activeDrawerPanel = selectedItem?.type === "stop" ? "stop" : selectedItem?.type === "vehicle" ? "vehicle" : selectedItem?.type === "scooter" ? "scooter" : searchLocation?.lnglat && panelMode !== "search" ? "location" : "empty";
  const drawerDetents = activeDrawerPanel === "stop" || activeDrawerPanel === "location" ? ["closed", "open", "full"] : ["closed", "open"];
  const allowDrawerFullSnap = drawerDetents.includes("full");
  const {
    y,
    isOpen,
    isFullOpen,
    drawerHeight,
    open,
    close,
    headerBind,
    bodyBind,
    openHeightRef: heightRef,
    closedHeightRef: closeRef,
    fullHeightRef
  } = useMobileDrawer({
    isDesktop,
    openHeight: height,
    closedHeight: closeHeight,
    fullHeight,
    scrollRef: mobileSheetBodyRef,
    detents: drawerDetents
  });
  const panelExpanded = isDesktop ? true : isOpen;
  const hideMobileMapOverlays = !isDesktop && drawerHeight > height + 32;
  const [isMounted, setIsMounted] = useState(false);
  const [isMapStyleLoaded, setIsMapStyleLoaded] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts?.load) {
      return;
    }
    void document.fonts.load('12px "Bus"');
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateViewport = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => {
      window.removeEventListener("resize", updateViewport);
    };
  }, []);
  useLayoutEffect(() => {
    if (!panelRef.current || !isDesktop || typeof ResizeObserver === "undefined") {
      return;
    }
    const updatePanelWidth = () => {
      if (!panelRef.current) return;
      setPanelWidth(panelRef.current.clientWidth || defaultPanelWidth);
    };
    updatePanelWidth();
    const observer = new ResizeObserver(updatePanelWidth);
    observer.observe(panelRef.current);
    return () => {
      observer.disconnect();
    };
  }, [isDesktop]);
  const selectedStopId = selectedItem?.type === "stop" ? selectedItem.id : null;
  const {
    data: stopItemDetails,
    isLoading: stopDetailsIsLoading
  } = useSWR(selectedStopId ? `/api/stops/${encodeURIComponent(selectedStopId)}` : null, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 60 * 60 * 1000
  });
  const {
    data: stopDepartures,
    isLoading: stopDeparturesIsLoading
  } = useSWR(selectedStopId ? `/api/stopdepartures/${encodeURIComponent(selectedStopId)}` : null, fetcher, {
    refreshInterval: 20000,
    revalidateOnFocus: true,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    dedupingInterval: 10000
  });
  const stopPanelIsLoading = stopDetailsIsLoading || stopDeparturesIsLoading;
  const {
    itemDetails,
    itemDetailsIsLoading,
    selectedVehicleTripDetails,
    selectedVehicleRouteDetails,
    busVehicleDetails,
    busVehicleDetailsLoading,
    selectedVehicleUpdate,
    selectedTripData,
    selectedTripShape
  } = useSelectedVehicleDetails({
    selectedItem,
    isDark,
    routeDisplayOptions: wellingtonRouteDisplayOptions
  });
  const selectedVehicleNextStop = getVehicleNextStop(selectedVehicleUpdate, selectedTripData?.stops);
  const selectedVehicleNextStopName = selectedVehicleNextStop ? getPreferredStopName(preferredStopNames, selectedVehicleNextStop.stopId, selectedVehicleNextStop.stopId) || selectedVehicleNextStop.stopName : null;
  const selectedVehicleUsesLocationFallback = selectedItem?.type === "vehicle" && !selectedVehicleNextStopName && hasAffirmativelyUnknownVehicleStop(selectedVehicleUpdate);
  const selectedVehicleNextStopType = selectedVehicleNextStop ? checkStopType(selectedVehicleNextStop.stopId) : "bus";
  const roundedSelectedLocationCoordinates = selectedItem?.type === "scooter" || selectedVehicleUsesLocationFallback ? getRoundedReverseGeocodeCoordinates(selectedItem) : null;
  const selectedLocationReverseGeocodeKey = roundedSelectedLocationCoordinates && `/api/mapsearch/reverse?lng=${encodeURIComponent(roundedSelectedLocationCoordinates.lng)}&lat=${encodeURIComponent(roundedSelectedLocationCoordinates.lat)}`;
  const {
    data: selectedLocationReverseGeocode
  } = useSWR(selectedLocationReverseGeocodeKey, fetcher, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    dedupingInterval: 7 * 24 * 60 * 60 * 1000
  });
  const selectedLocationName = getUsefulReverseGeocodeName(selectedLocationReverseGeocode);
  const selectedMicromobilityLocationName = selectedItem?.type === "scooter" ? selectedLocationName : null;
  const selectedVehicleLocationName = selectedVehicleUsesLocationFallback ? selectedLocationName : null;
  const getLayoutState = (live = false) => getMainMapLayout({
    width: live ? window.innerWidth : viewport.width,
    height: live ? window.innerHeight : viewport.height,
    panelExpanded: live ? panelExpandedRef.current : panelExpanded,
    panelWidth: live ? panelRef.current?.clientWidth : panelWidth,
    openHeight: live ? heightRef.current : height,
    closedHeight: live ? closeRef.current : closeHeight
  });
  const getMapPadding = (layout = getLayoutState(), open) => getMainMapPadding(layout, open);
  const [lastMapCenter, setLastMapCenter,, isLastMapCenterHydrated] = useLocalStorage(getStorageKey("map_center"), mapCentreDefault);
  const initialMapView = getMainMapInitialView({
    coordinate: initialMapSelection.coordinate,
    lastMapCenter,
    fallback: mapCentreDefault
  });
  const [geolocateFunction, setGeolocateFunction] = useState(null);
  const [geolocatePermissionState, setGeolocatePermissionState] = useState();
  const [geolocating, setGeolocating] = useState(false);
  const [geolocated, setGeolocated] = useState(false);
  const longPressTimeoutRef = useRef(null);
  const nativeLocationPermissionRequestRef = useRef(null);
  if (!nativeLocationPermissionRequestRef.current) {
    nativeLocationPermissionRequestRef.current = createNativeLocationPermissionRequest();
  }
  const longPressStartRef = useRef(null);
  const mousePinTimeoutRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const skipNextUrlPinRecenterRef = useRef(false);
  const skipNextUrlLiveRecenterRef = useRef(false);
  const autoEnabledLiveQueryRef = useRef({
    vehicle: null,
    scooter: null
  });
  const cameraControllerRef = useRef(null);
  const [dragged, setDragged] = useState(false);
  const [isUserCentered, setIsUserCentered] = useState(false);
  const [rotated, setRotated] = useState(false);
  const [mapZoom, setMapZoom] = useState(() => Number(mapCentreDefault?.zoom) || 0);
  const [rotation, setRotation] = useState(false);
  const [showVehicles, setShowVehicles] = useLocalStorage("show_vehicles", true);
  const [showScooters, setShowScooters] = useLocalStorage("show_scooters", false);
  const {
    data: recentStops,
    isLoading: recentStopsIsLoading
  } = useRecentStopSummaries({
    recents,
    version: process.env.NEXT_PUBLIC_APP_VERSION
  });
  const hasRecentStops = Array.isArray(recents) && recents.length > 0;
  useTrackRecentStop({
    isFallback: false,
    setRecents,
    stopId: selectedItem?.type === "stop" ? selectedItem.id : null
  });
  const triggerGeolocation = useCallback(() => {
    if (!geolocateFunction?.trigger) {
      setGeolocating(false);
      return;
    }
    const triggered = geolocateFunction.trigger();
    if (triggered === false) {
      setGeolocating(false);
      return;
    }
    if (!geolocated && !geolocating) {
      setGeolocated(false);
      setGeolocating(true);
    }
    if (geolocated && !dragged) {
      setGeolocated(false);
      setGeolocating(false);
    }
  }, [dragged, geolocateFunction, geolocated, geolocating]);
  const triggerGeolocationRef = useRef(triggerGeolocation);
  useEffect(() => {
    triggerGeolocationRef.current = triggerGeolocation;
  }, [triggerGeolocation]);
  const handleGeolocateClick = useCallback(() => {
    if (nativeLocationPermissionRequestRef.current.shouldRetryBrowserGeolocation()) {
      triggerGeolocation();
      return;
    }
    if (typeof window !== "undefined" && hasNativeTwaMessagePort()) {
      if (window.sessionStorage.getItem(androidLocationPermissionSessionKey) !== null) return;
      const requestId = String(Date.now());
      nativeLocationPermissionRequestRef.current.begin();
      window.sessionStorage.setItem(androidLocationPermissionSessionKey, requestId);
      window.setTimeout(() => {
        if (window.sessionStorage.getItem(androidLocationPermissionSessionKey) === requestId) {
          window.sessionStorage.removeItem(androidLocationPermissionSessionKey);
          nativeLocationPermissionRequestRef.current.markNativeResultUnavailable();
          setGeolocating(false);
        }
      }, 10_000);
      requestNativeLocationPermission();
      return;
    }
    triggerGeolocation();
  }, [triggerGeolocation]);
  useEffect(() => {
    const openNativeLocationPermissionActivity = () => {
      if (!window.sessionStorage.getItem(androidLocationPermissionSessionKey)) {
        return;
      }
      window.sessionStorage.removeItem(androidLocationPermissionSessionKey);
      window.location.assign(`catchy://${window.location.host}/location-permission`);
    };
    window.addEventListener(nativeLocationPermissionRequestEvent, openNativeLocationPermissionActivity);
    window.addEventListener(nativeLocationPermissionAuthorizedEvent, openNativeLocationPermissionActivity);
    const completeNativeLocationPermission = event => {
      window.sessionStorage.removeItem(androidLocationPermissionSessionKey);
      const result = event.detail?.result;
      const nativeResult = nativeLocationPermissionRequestRef.current.onNativeResult(result);
      if (nativeResult.denied) {
        setGeolocating(false);
        setGeolocatePermissionState("denied");
        return;
      }
      if (result === "granted" && nativeResult.accepted) {
        setGeolocatePermissionState("granted");
        window.setTimeout(() => triggerGeolocationRef.current(), 0);
        return;
      }
      setGeolocating(false);
    };
    window.addEventListener(nativeLocationPermissionResultEvent, completeNativeLocationPermission);
    return () => {
      window.removeEventListener(nativeLocationPermissionRequestEvent, openNativeLocationPermissionActivity);
      window.removeEventListener(nativeLocationPermissionAuthorizedEvent, openNativeLocationPermissionActivity);
      window.removeEventListener(nativeLocationPermissionResultEvent, completeNativeLocationPermission);
    };
  }, []);
  const cancelLongPress = useCallback(() => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
    longPressStartRef.current = null;
  }, []);
  const cancelPendingMousePin = useCallback(() => {
    if (mousePinTimeoutRef.current) {
      clearTimeout(mousePinTimeoutRef.current);
      mousePinTimeoutRef.current = null;
    }
  }, []);
  const addSearchLocationMarker = useCallback(async location => {
    if (!map.current) return;
    if (!location) {
      if (map.current.getLayer("searchLocationMarker")) {
        map.current.removeLayer("searchLocationMarker");
      }
      if (map.current.getSource("searchLocationMarker")) {
        map.current.removeSource("searchLocationMarker");
      }
      return;
    }
    await ensureCoreMapImages(map.current, {
      isDark,
      preferSatellite
    });
    const source = map.current.getSource("searchLocationMarker");
    if (source) {
      source.setData({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: location.lnglat
        },
        properties: {
          title: location.name
        }
      });
    } else {
      map.current.addSource("searchLocationMarker", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: location.lnglat
          },
          properties: {
            title: location.name
          }
        }
      });
      map.current.addLayer({
        id: "searchLocationMarker",
        type: "symbol",
        source: "searchLocationMarker",
        layout: {
          "symbol-placement": "point",
          "icon-image": ["step", ["zoom"], "", 5, "search-position"],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-padding": 10,
          "icon-anchor": "bottom",
          "icon-size": searchLocationIconSizeExpression
        },
        paint: {
          "icon-translate": [0, 0]
        }
      });
    }
  }, [isDark, preferSatellite]);
  const isLocationPanel = !selectedItem && searchLocation?.lnglat && panelMode !== "search";
  const isStopPanel = selectedItem?.type === "stop" && panelMode !== "search";
  const shouldCapMobilePanelHeight = isLocationPanel || isStopPanel;
  const getRecenterTarget = () => {
    const selectedTarget = createMapRecenterTarget([selectedItem?.lng, selectedItem?.lat], selectedItem?.type, {
      rejectZeroPair: true
    });
    if (selectedTarget) return selectedTarget;
    if (isLocationPanel) {
      return createMapRecenterTarget(searchLocation.lnglat, "location", {
        rejectZeroPair: true
      });
    }
    return null;
  };
  const getLiveRecenterTarget = () => {
    const selectedTarget = createMapRecenterTarget([selectedItemRef.current?.lng, selectedItemRef.current?.lat], selectedItemRef.current?.type, {
      rejectZeroPair: true
    });
    if (selectedTarget) return selectedTarget;
    if (!selectedItemRef.current && searchLocationRef.current?.lnglat && panelModeRef.current !== "search") {
      return createMapRecenterTarget(searchLocationRef.current.lnglat, "location", {
        rejectZeroPair: true
      });
    }
    return null;
  };
  const selectedItemRef = useRef();
  const searchLocationRef = useRef(searchLocation);
  const isDesktopRef = useRef(isDesktop);
  const panelModeRef = useRef(panelMode);
  const panelExpandedRef = useRef(panelExpanded);
  const livePanelWidthRef = useRef(panelWidth);
  const draggedRef = useRef(dragged);
  useEffect(() => {
    selectedItemRef.current = selectedItem;
    searchLocationRef.current = searchLocation;
    isDesktopRef.current = isDesktop;
    panelModeRef.current = panelMode;
    panelExpandedRef.current = panelExpanded;
    livePanelWidthRef.current = panelWidth;
    draggedRef.current = dragged;
  }, [selectedItem, searchLocation, isDesktop, panelMode, panelExpanded, panelWidth, dragged]);

  // Utility: run fn only when the style is ready
  const waitForStyle = (mapInstance, whenReady) => {
    if (!mapInstance) return;
    if (mapInstance.isStyleLoaded()) {
      whenReady();
    } else {
      mapInstance.once("style.load", whenReady);
    }
  };
  const resetStopIcons = () => {
    waitForStyle(map.current, () => {
      if (!map.current.getLayer(mapStopsLayerId)) return;
      map.current.setLayoutProperty(mapStopsLayerId, "icon-image", buildStopIconExpression(isDark, preferSatellite, favourites));
    });
  };
  const applySelectedStopHighlight = (item = selectedItemRef.current) => {
    if (!map.current || item?.type !== "stop" || !map.current.getLayer(mapStopsLayerId) || !map.current.getLayer(mapActiveStopsLayerId)) {
      return false;
    }
    const selectedStopId = String(item.id);
    map.current.setFilter(mapStopsLayerId, ["!=", ["to-string", ["get", "stop_id"]], selectedStopId]);
    map.current.setFilter(mapActiveStopsLayerId, ["==", ["to-string", ["get", "stop_id"]], selectedStopId]);
    map.current.setLayoutProperty(mapActiveStopsLayerId, "visibility", "visible");
    resetStopIcons();
    return true;
  };
  useStopIcons(map, isMapStyleLoaded, isDark, preferSatellite, favourites);

  // The shared lifecycle is the sole style owner. Keep this identity stable
  // until the user changes theme so it cannot replace an in-flight cold load.
  const mapStyle = useMemo(() => getMaplibreStyle({
    isDark,
    preferSatellite
  }), [isDark, preferSatellite]);
  useEffect(() => {
    if (!isLastMapCenterHydrated) return;
    if (!navigator.geolocation) {
      setGeolocatePermissionState("unsupported");
    } else if (!navigator.permissions?.query) {
      setGeolocatePermissionState("unknown");
    } else {
      void navigator.permissions.query({
        name: "geolocation"
      }).then(permission => {
        setGeolocatePermissionState(permission.state);
      }).catch(() => setGeolocatePermissionState("unknown"));
    }
    map.current = createCatchyMap(MapLibreMap, {
      container: mapContainerRef.current,
      style: null,
      pitch: 0,
      maxPitch: 0,
      maxZoom: mainMapMaxZoom,
      maxBounds: NEW_ZEALAND_MAP_BOUNDS,
      ...initialMapView
    });
    const initialPadding = getMapPadding(getLayoutState(true), initialMapSelection.hasSelection);
    cameraControllerRef.current = createMainMapCameraController(map.current, {
      initialView: {
        ...initialMapView,
        padding: initialPadding
      }
    });
    // Rotation is deliberate map navigation. Keep it enabled so the bearing
    // reset compass appears after a right-drag (desktop) or two-finger turn.
    map.current.dragRotate.enable();
    const canvasContainer = map.current.getCanvasContainer();
    const longPressDelayMs = 650;
    const moveTolerancePx = 12;
    const preventCanvasContextMenu = event => {
      event.preventDefault();
    };
    const resolveMissingStyleImage = async missingImageId => {
      if (!missingImageId || !map.current) {
        return;
      }
      if (coreMapImageIds.has(missingImageId)) {
        await ensureCoreMapImages(map.current, {
          isDark,
          preferSatellite
        });
        return;
      }
      if (missingImageId.startsWith("micromobility-")) {
        await ensureMicromobilityMarkerImages(map.current, scooterPositionsRef.current);
        return;
      }
      if (missingImageId.startsWith("route-")) {
        await ensureVehicleRouteBadgeImages({
          mapInstance: map.current,
          vehiclePositions: vehiclePositionsRef.current,
          pendingImageIds: pendingVehicleBadgeImageIdsRef,
          createRouteBadgeSvg
        });
      }
    };
    const getPointFromEvent = event => {
      const rect = canvasContainer.getBoundingClientRect();
      if ("touches" in event && event.touches.length > 0) {
        const touch = event.touches[0];
        return {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top
        };
      }
      if ("changedTouches" in event && event.changedTouches.length > 0) {
        const touch = event.changedTouches[0];
        return {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top
        };
      }
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    };
    const startLongPress = event => {
      if ("button" in event && event.button !== 0) {
        return;
      }
      const point = getPointFromEvent(event);
      longPressStartRef.current = point;
      cancelLongPress();
      longPressStartRef.current = point;
      longPressTimeoutRef.current = window.setTimeout(() => {
        const lngLat = map.current?.unproject([point.x, point.y]);
        if (!lngLat) return;
        suppressNextClickRef.current = true;
        handleLongPressLocation(lngLat.lng, lngLat.lat);
        longPressTimeoutRef.current = null;
      }, longPressDelayMs);
    };
    const maybeCancelLongPressOnMove = event => {
      if (!longPressStartRef.current) return;
      const point = getPointFromEvent(event);
      const dx = point.x - longPressStartRef.current.x;
      const dy = point.y - longPressStartRef.current.y;
      if (Math.hypot(dx, dy) > moveTolerancePx) {
        cancelLongPress();
      }
    };
    canvasContainer.addEventListener("touchstart", startLongPress, {
      passive: true
    });
    canvasContainer.addEventListener("touchmove", maybeCancelLongPressOnMove, {
      passive: true
    });
    canvasContainer.addEventListener("touchend", cancelLongPress);
    canvasContainer.addEventListener("touchcancel", cancelLongPress);
    canvasContainer.addEventListener("contextmenu", preventCanvasContextMenu);
    map.current.on("dragstart", event => {
      cancelLongPress();
      cancelPendingMousePin();
      if (!event?.originalEvent && cameraControllerRef.current?.isProgrammatic()) {
        return;
      }
      cameraControllerRef.current?.handleUserMove();
      setDragged(true);
      setIsUserCentered(false);
    });
    map.current.on("zoomstart", event => {
      cancelLongPress();
      cancelPendingMousePin();
      if (!event?.originalEvent && cameraControllerRef.current?.isProgrammatic()) {
        return;
      }
      cameraControllerRef.current?.handleUserMove();
      setDragged(true);
      setIsUserCentered(false);
    });

    // Box zoom emits its own user event before the fitted camera movement.
    // Treat it as manual even when a previous programmatic move is settling.
    map.current.on("boxzoomstart", () => {
      cancelLongPress();
      cancelPendingMousePin();
      cameraControllerRef.current?.handleUserMove();
      setDragged(true);
      setIsUserCentered(false);
    });
    map.current.on("moveend", () => {
      cameraControllerRef.current?.handleMoveEnd();
      setMapZoom(map.current.getZoom());
      setLastMapCenter({
        center: map.current.getCenter(),
        zoom: map.current.getZoom()
      });
    });
    map.current.on("rotatestart", () => {
      setRotated(true);
    });
    map.current.on("rotate", () => {
      setRotation(map.current.getBearing());
    });
    map.current.on("load", async function () {
      const geolocate = new GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        fitBoundsOptions: {
          maxZoom: 16,
          offset: [0, -40]
        },
        trackUserLocation: true,
        showAccuracyCircle: false,
        showUserHeading: true
      });
      map.current.addControl(geolocate);
      const geolocateControlButton = mapContainerRef.current?.querySelector(".maplibregl-ctrl-geolocate");
      if (geolocateControlButton) {
        geolocateControlButton.style.display = "none";
        geolocateControlButton.tabIndex = -1;
        geolocateControlButton.setAttribute("aria-hidden", "true");
      }
      setGeolocateFunction(geolocate);
      geolocate.on("geolocate", () => {
        nativeLocationPermissionRequestRef.current.onMapLibreSuccess();
        setGeolocating(false);
        setGeolocated(true);
        setDragged(false);
      });
      geolocate.on("trackuserlocationstart", () => {
        setGeolocating(false);
        setGeolocated(true);
        setDragged(false);
      });
      geolocate.on("trackuserlocationend", () => {
        setGeolocating(false);
      });
      geolocate.on("error", error => {
        setGeolocating(false);
        const permissionError = Number(error?.code ?? error?.error?.code) === 1;
        const nativeError = nativeLocationPermissionRequestRef.current.onMapLibreError(error?.code ?? error?.error?.code);
        if (permissionError && !nativeError.suppress) {
          setGeolocatePermissionState("denied");
        }
      });
    });
    map.current.setMissingStyleImageResolver(resolveMissingStyleImage);
    return () => {
      cancelLongPress();
      cancelPendingMousePin();
      canvasContainer.removeEventListener("touchstart", startLongPress);
      canvasContainer.removeEventListener("touchmove", maybeCancelLongPressOnMove);
      canvasContainer.removeEventListener("touchend", cancelLongPress);
      canvasContainer.removeEventListener("touchcancel", cancelLongPress);
      canvasContainer.removeEventListener("contextmenu", preventCanvasContextMenu);
      map.current.setMissingStyleImageResolver(null);
      map.current.remove();
      cameraControllerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLastMapCenterHydrated]);
  const {
    readyRevision: mainMapReadyRevision,
    vehiclePositions,
    vehiclePositionsError,
    vehiclePositionsIsLoading,
    refreshVehiclePositions,
    scooterPositions,
    scooterPositionsError,
    scooterPositionsIsLoading
  } = useMainMapRuntime({
    mapRef: map,
    style: mapStyle,
    liveData: {
      showVehicles,
      showScooters,
      buildVehicleFeatureCollection,
      buildScooterFeatureCollection,
      staleAfterMs: defaultVehicleStaleAfterMs
    },
    runtimeState: {
      isDark,
      preferSatellite,
      favourites,
      selection: selectedItem,
      stopLayerIds: {
        stops: mapStopsLayerId,
        activeStops: mapActiveStopsLayerId
      },
      stopSourceConfig: mapStopSourceConfig,
      selectedTripShape,
      syncStopSelection: applySelectedStopHighlight,
      syncSearchLocation: () => addSearchLocationMarker(searchLocation),
      ensureVehicleBadges: currentVehiclePositions => ensureVehicleRouteBadgeImages({
        mapInstance: map.current,
        vehiclePositions: currentVehiclePositions,
        pendingImageIds: pendingVehicleBadgeImageIdsRef,
        createRouteBadgeSvg
      }),
      ensureScooterImages: currentScooterPositions => ensureMicromobilityMarkerImages(map.current, currentScooterPositions)
    }
  });
  const selectMapStyle = useCallback(style => {
    setPreferSatellite(style === "hybrid");
  }, [setPreferSatellite]);
  useEffect(() => {
    vehiclePositionsRef.current = vehiclePositions;
  }, [vehiclePositions]);
  useEffect(() => {
    scooterPositionsRef.current = scooterPositions;
  }, [scooterPositions]);
  useEffect(() => {
    setIsMapStyleLoaded(mainMapReadyRevision > 0);
  }, [mainMapReadyRevision]);
  const resetActiveStops = () => {
    const mapInstance = map.current;
    if (mapInstance?.getLayer(mapStopsLayerId) && mapInstance.getLayer(mapActiveStopsLayerId)) {
      mapInstance.setFilter(mapStopsLayerId, null);
      mapInstance.setFilter(mapActiveStopsLayerId, null);
      mapInstance.setLayoutProperty(mapActiveStopsLayerId, "visibility", "none");
    }
  };
  const getBaseMapQuery = useCallback(() => getBaseMapQueryFrom(queryRef.current), [queryRef]);
  const clearLiveLayerSelection = (type, queryKey) => {
    const hasSelectedType = selectedItemRef.current?.type === type;
    const hasQuerySelection = queryRef.current?.[queryKey] !== undefined;
    if (!hasSelectedType && !hasQuerySelection) {
      return;
    }
    resetActiveStops();
    setSelectedFeatureId(null);
    setSelectedItem(null);
    setSearchLocation(null);
    setPanelMode("selection");
    if (!isDesktopRef.current) {
      close();
    }
    autoEnabledLiveQueryRef.current[queryKey] = getQueryValue(queryRef.current?.[queryKey]) || null;
    const nextQuery = getBaseMapQueryFrom(queryRef.current);
    queryRef.current = nextQuery;
    updateMapQuery(nextQuery, "replace");
  };
  const handleVehicleVisibilityToggle = () => {
    const nextShowVehicles = !showVehicles;
    setShowVehicles(nextShowVehicles);
    if (!nextShowVehicles) {
      clearLiveLayerSelection("vehicle", "vehicle");
    }
  };
  const handleScooterVisibilityToggle = () => {
    const nextShowScooters = !showScooters;
    setShowScooters(nextShowScooters);
    if (!nextShowScooters) {
      clearLiveLayerSelection("scooter", "scooter");
    }
  };
  const handleMapBack = useCallback(() => {
    const isInitialLoad = typeof window !== "undefined" && sessionStorage.getItem("isInitialLoad") === "true";
    if (!isInitialLoad) {
      router.back();
      return;
    }
    router.push(getMapOriginHref(getQueryValue(queryRef.current.from)));
  }, [queryRef, router]);
  const getHistoryMethodForState = useCallback((nextState, {
    allowNestedPush = false
  } = {}) => {
    const currentState = getMapHistoryState(queryRef.current);
    if (isPlainMapHistoryState(currentState)) {
      return "push";
    }
    if (allowNestedPush && isPinOnlyHistoryState(currentState) && nextState.stop) {
      return "push";
    }
    return "replace";
  }, [queryRef]);
  useEffect(() => {
    const mapInstance = map.current;
    const stopId = query.stop;
    const stopLat = getQueryNumber(query.lat);
    const stopLng = getQueryNumber(query.lng);
    const selectedStopId = selectedItemRef.current?.id;
    const alreadySelectedStop = selectedItemRef.current?.type === "stop" && selectedStopId !== undefined && String(selectedStopId) === String(stopId);
    if (!router.isReady || !isLastMapCenterHydrated || !mapInstance || stopId === undefined || stopLat === null || stopLng === null) {
      return;
    }
    recentreFromUrl(stopLng, stopLat, undefined, true);
    if (alreadySelectedStop) {
      return;
    }

    // The URL already contains the selected stop and its coordinates. Restoring
    // from it must not wait for (or query) a rendered tile: on a fresh map the
    // stop layer can be absent until the style finishes loading.
    setSearchLocation(null);
    setPanelMode("selection");
    setSelectedItem({
      type: "stop",
      id: stopId,
      stopCode: stopId,
      name: stopId,
      stopType: checkStopType(stopId),
      lng: stopLng,
      lat: stopLat
    });
    setSelectedFeatureId(stopId);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, isLastMapCenterHydrated, query.stop, query.lat, query.lng]);
  useEffect(() => {
    if (!router.isReady) return;
    const pin = getQueryValue(query.pin);
    const lat = getQueryNumber(query.lat);
    const lng = getQueryNumber(query.lng);
    if (query.stop !== undefined) {
      return;
    }
    if (pin === "1" && lat !== null && lng !== null) {
      const shouldSkipRecenter = skipNextUrlPinRecenterRef.current;
      resetActiveStops();
      setSelectedFeatureId(null);
      setSelectedItem(null);
      setPanelMode("location");
      setSearchLocation(currentLocation => {
        const hasSameLocation = currentLocation?.lnglat?.[0] === lng && currentLocation?.lnglat?.[1] === lat;
        if (hasSameLocation) {
          return {
            ...currentLocation,
            lnglat: [lng, lat]
          };
        }
        return {
          lnglat: [lng, lat]
        };
      });
      if (shouldSkipRecenter) {
        skipNextUrlPinRecenterRef.current = false;
        return;
      }
      recentreFromUrl(lng, lat, undefined, true);
      return;
    }
    if (query.vehicle !== undefined || query.scooter !== undefined) {
      if (skipNextUrlLiveRecenterRef.current) {
        skipNextUrlLiveRecenterRef.current = false;
        return;
      }
      if (lat !== null && lng !== null) {
        recentreFromUrl(lng, lat, undefined, true);
      }
      return;
    }
    resetActiveStops();
    setSelectedFeatureId(null);
    setSelectedItem(null);
    setSearchLocation(null);
    setPanelMode("selection");
    if (!isDesktopRef.current) {
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, query.pin, query.lat, query.lng, query.stop, query.vehicle, query.scooter]);
  const handleClick = event => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    const clickableLayers = [mapStopsLayerId, vehicleLayerId, selectedVehicleLayerId, selectedScooterLayerId, scooterLayerId, scooterHitLayerId, "searchLocationMarker"].filter(layerId => map.current?.getLayer(layerId));
    const features = map.current.queryRenderedFeatures(event.point, {
      layers: clickableLayers
    });
    if (!features.length) {
      const originalEvent = event.originalEvent;
      const isTouchEvent = typeof TouchEvent !== "undefined" && originalEvent instanceof TouchEvent || originalEvent?.pointerType === "touch";
      const isMouseEvent = !isTouchEvent && typeof MouseEvent !== "undefined" && originalEvent instanceof MouseEvent;
      const hasExistingSelection = Boolean(selectedItemRef.current) || Boolean(searchLocationRef.current);
      if (isMouseEvent && !hasExistingSelection) {
        cancelPendingMousePin();
        mousePinTimeoutRef.current = window.setTimeout(() => {
          mousePinTimeoutRef.current = null;
          handleLongPressLocation(event.lngLat.lng, event.lngLat.lat);
        }, 250);
        return;
      }
      cancelPendingMousePin();
      setSelectedItem(null);
      setSearchLocation(null);
      setPanelMode("selection");
      setSelectedFeatureId(null);
      resetActiveStops();
      if (!isDesktopRef.current) {
        close();
      }
      const nextQuery = getBaseMapQuery();
      queryRef.current = nextQuery;
      updateMapQuery(nextQuery);
      return;
    }
    cancelPendingMousePin();
    const feature = getClickedMapFeature({
      features,
      selectedItem: selectedItemRef.current,
      stopLayerId: mapStopsLayerId
    });
    switch (feature.layer.id) {
      case scooterHitLayerId:
      case scooterLayerId:
      case selectedScooterLayerId:
        handleScooterClick(feature);
        break;
      case mapStopsLayerId:
        handleStopClick(feature);
        break;
      case vehicleLayerId:
      case pointerLayerId:
      case selectedVehicleLayerId:
        handleVehicleClick(feature);
        break;
      case "searchLocationMarker":
        handleLocationMarkerClick(feature);
        break;
      default:
        break;
    }
  };
  const handleStopClick = feature => {
    handleStopSelect({
      stop_id: feature.properties.stop_id,
      stop_code: feature.properties.stop_code,
      stop_name: feature.properties.title,
      stop_lon: feature.geometry.coordinates[0],
      stop_lat: feature.geometry.coordinates[1]
    });
  };
  const handleStopSelect = (stop, {
    history,
    allowNestedPush = false
  } = {}) => {
    setPanelMode("selection");
    setSearchLocation(null);
    setSelectedItem({
      type: "stop",
      id: stop.stop_id,
      stopCode: stop.stop_code,
      name: stop.stop_name,
      stopType: stop.stop_type || checkStopType(stop.stop_id),
      lng: stop.stop_lon,
      lat: stop.stop_lat
    });
    setSelectedFeatureId(stop.stop_id);
    const {
      vehicle,
      scooter,
      pin,
      ...restQuery
    } = queryRef.current;
    const nextQuery = {
      ...restQuery,
      stop: stop.stop_id,
      lat: stop.stop_lat,
      lng: stop.stop_lon
    };
    queryRef.current = nextQuery;
    updateMapQuery(nextQuery, history ?? getHistoryMethodForState(getMapHistoryState(nextQuery), {
      allowNestedPush
    }));
    recentre(stop.stop_lon, stop.stop_lat, undefined, true);
    open();
  };
  const handleVehicleClick = feature => {
    handleLiveMapItemSelect(feature, liveMapSelectionConfigs[0]);
  };
  const handleScooterClick = feature => {
    handleLiveMapItemSelect(feature, liveMapSelectionConfigs[1]);
  };
  const handleLiveMapItemSelect = (feature, config) => {
    const selectedLiveItem = config.buildSelectedItem(feature);
    setPanelMode("selection");
    setSearchLocation(null);
    setSelectedFeatureId(null);
    resetActiveStops();
    setSelectedItem(selectedLiveItem);
    const restQuery = getBaseMapQueryFrom(queryRef.current);
    const nextQuery = {
      ...restQuery,
      [config.queryKey]: selectedLiveItem.id,
      lat: selectedLiveItem.lat,
      lng: selectedLiveItem.lng
    };
    queryRef.current = nextQuery;
    skipNextUrlLiveRecenterRef.current = true;
    updateMapQuery(nextQuery, getHistoryMethodForState(getMapHistoryState(nextQuery)));
    recentre(selectedLiveItem.lng, selectedLiveItem.lat, undefined, true);
    open();
  };
  const handleLocationSelect = (location, {
    history
  } = {}) => {
    setSearchLocation(location);
    setSelectedItem(null);
    setSelectedFeatureId(null);
    resetActiveStops();
    setPanelMode("location");
    const nextQuery = {
      ...getBaseMapQuery(),
      pin: "1",
      lat: location.lnglat[1],
      lng: location.lnglat[0]
    };
    queryRef.current = nextQuery;
    updateMapQuery(nextQuery, history ?? getHistoryMethodForState(getMapHistoryState(nextQuery)));
    if (!isDesktopRef.current) {
      open();
    }
  };
  const handleLongPressLocation = async (lng, lat) => {
    const fallbackName = formatDroppedPinName(lng, lat);
    const droppedLocation = {
      lnglat: [lng, lat],
      name: fallbackName
    };
    skipNextUrlPinRecenterRef.current = true;
    setDragged(false);
    setIsUserCentered(true);
    handleLocationSelect(droppedLocation);
    try {
      const response = await fetch(`/api/mapsearch/reverse?lng=${encodeURIComponent(lng)}&lat=${encodeURIComponent(lat)}`);
      if (!response.ok) {
        throw new Error(`Reverse geocode failed with ${response.status}`);
      }
      const result = await response.json();
      setSearchLocation(currentLocation => {
        if (!currentLocation || currentLocation.lnglat?.[0] !== lng || currentLocation.lnglat?.[1] !== lat) {
          return currentLocation;
        }
        return {
          ...currentLocation,
          name: result?.name || fallbackName
        };
      });
      const nextQuery = {
        ...getBaseMapQuery(),
        pin: "1",
        lat,
        lng
      };
      queryRef.current = nextQuery;
      updateMapQuery(nextQuery, "replace");
    } catch (error) {
      console.error("Failed to resolve long-press location:", error);
    }
    if (!isDesktopRef.current) {
      open();
    }
  };
  const handleLocationMarkerClick = feature => {
    const fallbackName = formatDroppedPinName(feature.geometry.coordinates[0], feature.geometry.coordinates[1]);
    const location = {
      name: feature.properties.title || searchLocationRef.current?.name || fallbackName,
      lnglat: feature.geometry.coordinates
    };
    handleLocationSelect(location, {
      history: "replace"
    });
    recentre(feature.geometry.coordinates[0], feature.geometry.coordinates[1], undefined, true);
    if (!isDesktopRef.current) {
      open();
    }
  };
  const handleNearbyStopSelect = stop => {
    handleStopSelect(stop, {
      history: searchLocationRef.current ? "push" : undefined,
      allowNestedPush: Boolean(searchLocationRef.current)
    });
  };
  const activeMapStyle = preferSatellite ? "hybrid" : "standard";
  const mapStyleMenu = <OptionsMenu label="Change map style" variant="map" trigger={<Layers aria-hidden="true" className="h-[1.25rem] w-[1.25rem]" />}>
      <div className="flex flex-col text-lg">
        {[["standard", "Standard"], ["hybrid", "Hybrid"]].map(([style, label]) => <MenuItem key={style} as="button" onClick={() => selectMapStyle(style)} className="flex w-full items-center justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3">
            <span>{label}</span>
            {activeMapStyle === style && <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Current</span>}
          </MenuItem>)}
      </div>
    </OptionsMenu>;
  const mobileUrlSelectionKey = `${getQueryValue(query.stop) || ""}|${getQueryValue(query.vehicle) || ""}|${getQueryValue(query.scooter) || ""}|${getQueryValue(query.pin) || ""}|${getQueryValue(query.lat) || ""}|${getQueryValue(query.lng) || ""}`;
  useEffect(() => {
    if (!router.isReady || isDesktopRef.current) return;
    if (query.stop !== undefined || query.vehicle !== undefined || query.scooter !== undefined || getQueryValue(query.pin) === "1") {
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileUrlSelectionKey, router.isReady]);
  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance) {
      return undefined;
    }
    const clickHandler = event => {
      handleClick(event);
    };
    const doubleClickHandler = () => {
      cancelPendingMousePin();
    };
    const mouseEnterHandler = () => {
      mapInstance.getCanvas().style.cursor = "pointer";
    };
    const mouseLeaveHandler = () => {
      mapInstance.getCanvas().style.cursor = "";
    };
    const pointerCursorLayers = [mapStopsLayerId, vehicleLayerId, selectedVehicleLayerId, scooterHitLayerId, scooterLayerId, selectedScooterLayerId, "searchLocationMarker"];
    mapInstance.on("click", clickHandler);
    mapInstance.on("dblclick", doubleClickHandler);
    pointerCursorLayers.forEach(layerId => {
      mapInstance.on("mouseenter", layerId, mouseEnterHandler);
      mapInstance.on("mouseleave", layerId, mouseLeaveHandler);
    });
    return () => {
      mapInstance.off("click", clickHandler);
      mapInstance.off("dblclick", doubleClickHandler);
      pointerCursorLayers.forEach(layerId => {
        mapInstance.off("mouseenter", layerId, mouseEnterHandler);
        mapInstance.off("mouseleave", layerId, mouseLeaveHandler);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, isMapStyleLoaded]);
  const {
    data: nearbyStops,
    isLoading: nearbyStopsIsLoading
  } = useSWR(searchLocation?.lnglat ? `/api/stops/near/${searchLocation.lnglat[1]}/${searchLocation.lnglat[0]}` : null, fetcher, {
    revalidateOnFocus: false
  });
  useLayoutEffect(() => {
    if (isDesktop || !mobileSheetHeaderRef.current || !mobileSheetBodyRef.current || !mobileSheetBodyContentRef.current) {
      return;
    }
    if (typeof ResizeObserver === "undefined") return;
    const updateSheetMeasurements = () => {
      if (!mobileSheetHeaderRef.current || !mobileSheetBodyRef.current || !mobileSheetBodyContentRef.current) {
        return;
      }
      const headerHeight = mobileSheetHeaderRef.current.getBoundingClientRect().height || 0;
      const bodyContentHeight = mobileSheetBodyContentRef.current.getBoundingClientRect().height || 0;
      const shouldCapBodyHeight = shouldCapMobilePanelHeight;
      const roundedBodyContentHeight = Math.round(bodyContentHeight);
      const defaultOpenHeight = getInitialMobileOpenHeight(viewportHeight);
      const naturalOpenHeight = Math.round(headerHeight + roundedBodyContentHeight);
      const cappedOpenHeight = !allowDrawerFullSnap ? Math.min(naturalOpenHeight, defaultOpenHeight) : naturalOpenHeight;
      const openBodyHeight = shouldCapBodyHeight ? Math.max(mobileBodyOpenMinHeight, Math.min(roundedBodyContentHeight, mobileBodyMaxHeight)) : Math.max(0, cappedOpenHeight - Math.round(headerHeight));
      const closedBodyHeight = Math.min(mobileBodyClosedMinHeight, openBodyHeight);
      const nextHeight = Math.round(headerHeight + openBodyHeight);
      const nextCloseHeight = Math.round(headerHeight + closedBodyHeight);
      const nextFullHeight = Math.max(nextHeight, Math.round(viewportHeight - mobileDrawerFullTopGap));
      setHeight(nextHeight);
      setCloseHeight(nextCloseHeight);
      setFullHeight(nextFullHeight);
    };
    updateSheetMeasurements();
    const observer = new ResizeObserver(updateSheetMeasurements);
    observer.observe(mobileSheetHeaderRef.current);
    observer.observe(mobileSheetBodyRef.current);
    observer.observe(mobileSheetBodyContentRef.current);
    return () => {
      observer.disconnect();
    };
  }, [allowDrawerFullSnap, isDesktop, mobileBodyMaxHeight, shouldCapMobilePanelHeight, viewportHeight]);
  useEffect(() => {
    const scroller = isDesktop ? desktopPanelBodyRef.current : mobileSheetBodyRef.current;
    if (!scroller) return;
    const updateScrollState = () => {
      setPanelScrolled(scroller.scrollTop > 4);
    };
    updateScrollState();
    scroller.addEventListener("scroll", updateScrollState, {
      passive: true
    });
    return () => {
      scroller.removeEventListener("scroll", updateScrollState);
    };
  }, [isDesktop, panelMode, selectedItem, searchLocation, isOpen]);
  useEffect(() => {
    const targets = [mobileChromeRef.current, mobileMapRef.current, mobileInlineRecentreRef.current].filter(Boolean);
    if (!targets.length) return;
    if (!isDesktop && isFullOpen) {
      targets.forEach(element => {
        element.setAttribute("inert", "");
        element.setAttribute("aria-hidden", "true");
      });
      return;
    }
    targets.forEach(element => {
      element.removeAttribute("inert");
      element.removeAttribute("aria-hidden");
    });
  }, [isDesktop, isFullOpen]);
  useEffect(() => {
    const vehicleQuery = getQueryValue(query.vehicle) || null;
    const scooterQuery = getQueryValue(query.scooter) || null;
    if (vehicleQuery && autoEnabledLiveQueryRef.current.vehicle !== vehicleQuery) {
      autoEnabledLiveQueryRef.current.vehicle = vehicleQuery;
      if (!showVehicles) {
        setShowVehicles(true);
      }
    } else if (!vehicleQuery) {
      autoEnabledLiveQueryRef.current.vehicle = null;
    }
    if (scooterQuery && autoEnabledLiveQueryRef.current.scooter !== scooterQuery) {
      autoEnabledLiveQueryRef.current.scooter = scooterQuery;
      if (!showScooters) {
        setShowScooters(true);
      }
    } else if (!scooterQuery) {
      autoEnabledLiveQueryRef.current.scooter = null;
    }
  }, [query.vehicle, query.scooter, showVehicles, showScooters, setShowVehicles, setShowScooters]);
  useEffect(() => {
    if (!showVehicles && selectedItem?.type === "vehicle") {
      setSelectedItem(null);
    }
  }, [showVehicles, selectedItem]);
  useEffect(() => {
    if (!showScooters && selectedItem?.type === "scooter") {
      setSelectedItem(null);
    }
  }, [showScooters, selectedItem]);
  useEffect(() => {
    if (selectedItem?.type !== "vehicle" || !vehiclePositions?.features) {
      return;
    }
    const matchedVehicle = vehiclePositions.features.find(feature => feature?.properties.vehicleId === selectedItem.id);
    if (!matchedVehicle) {
      return;
    }
    const nextLng = matchedVehicle.geometry?.coordinates?.[0];
    const nextLat = matchedVehicle.geometry?.coordinates?.[1];
    const nextOccupancyStatus = matchedVehicle.properties?.occupancyStatus;
    const nextTripId = matchedVehicle.properties?.tripId;
    const nextStartDate = matchedVehicle.properties?.startDate;
    if (selectedItem.lng === nextLng && selectedItem.lat === nextLat && selectedItem.occupancyStatus === nextOccupancyStatus && selectedItem.trip_id === nextTripId && selectedItem.startDate === nextStartDate) {
      return;
    }
    setSelectedItem(current => {
      if (!current || current.type !== "vehicle" || current.id !== selectedItem.id) {
        return current;
      }
      return {
        ...current,
        lng: nextLng,
        lat: nextLat,
        occupancyStatus: nextOccupancyStatus,
        trip_id: nextTripId,
        startDate: nextStartDate
      };
    });
  }, [selectedItem, vehiclePositions]);
  useEffect(() => {
    if (selectedItem?.type !== "scooter" || !scooterPositions?.features) {
      return;
    }
    const matchedScooter = scooterPositions.features.find(feature => feature?.properties.vehicleId === selectedItem.id);
    if (!matchedScooter) {
      return;
    }
    const nextLng = matchedScooter.geometry?.coordinates?.[0];
    const nextLat = matchedScooter.geometry?.coordinates?.[1];
    const nextProvider = matchedScooter.properties?.provider;
    const nextFormFactor = matchedScooter.properties?.formFactor;
    const nextMarkerImageId = matchedScooter.properties?.markerImageId;
    const nextRangeMeters = matchedScooter.properties?.rangeMeters;
    const nextLastReported = matchedScooter.properties?.lastReported;
    const nextRentalUri = matchedScooter.properties?.rentalUri;
    const nextStationName = matchedScooter.properties?.stationName;
    const nextCount = matchedScooter.properties?.count;
    if (selectedItem.lng === nextLng && selectedItem.lat === nextLat && selectedItem.provider === nextProvider && selectedItem.formFactor === nextFormFactor && selectedItem.markerImageId === nextMarkerImageId && selectedItem.rangeMeters === nextRangeMeters && selectedItem.lastReported === nextLastReported && selectedItem.rentalUri === nextRentalUri && selectedItem.stationName === nextStationName && selectedItem.count === nextCount) {
      return;
    }
    setSelectedItem(current => {
      if (!current || current.type !== "scooter" || current.id !== selectedItem.id) {
        return current;
      }
      return {
        ...current,
        lng: nextLng,
        lat: nextLat,
        provider: nextProvider,
        formFactor: nextFormFactor,
        markerImageId: nextMarkerImageId,
        rangeMeters: nextRangeMeters,
        lastReported: nextLastReported,
        rentalUri: nextRentalUri,
        stationName: nextStationName,
        count: nextCount
      };
    });
  }, [selectedItem, scooterPositions]);
  useEffect(() => {
    const positionsByQueryKey = {
      vehicle: vehiclePositions,
      scooter: scooterPositions
    };
    const queryIdsByQueryKey = {
      vehicle: getQueryValue(query.vehicle),
      scooter: getQueryValue(query.scooter)
    };
    const currentQueryIdsByQueryKey = {
      vehicle: getQueryValue(queryRef.current.vehicle),
      scooter: getQueryValue(queryRef.current.scooter)
    };
    for (const config of liveMapSelectionConfigs) {
      const queryId = queryIdsByQueryKey[config.queryKey];
      const currentQueryId = currentQueryIdsByQueryKey[config.queryKey];
      if (!queryId || queryId !== currentQueryId || !positionsByQueryKey[config.queryKey]?.features?.length) {
        continue;
      }
      if (selectedItem?.type === config.type && selectedItem.id === queryId) {
        return;
      }
      const feature = getFeatureByVehicleId(positionsByQueryKey[config.queryKey], queryId);
      if (!feature) {
        continue;
      }
      setPanelMode("selection");
      setSearchLocation(null);
      setSelectedItem(config.buildSelectedItem(feature));
      return;
    }
  }, [query.vehicle, query.scooter, selectedItem, vehiclePositions, scooterPositions, queryRef]);
  useEffect(() => {
    if (selectedItem?.type === "vehicle" && !dragged && !geolocated && vehiclePositions) {
      const vehicle = vehiclePositions.features.find(feature => feature?.properties.vehicleId === selectedItem.id);
      if (vehicle) {
        const coordinate = normalizeCoordinate(vehicle?.geometry?.coordinates, {
          rejectZeroPair: true
        });
        if (coordinate) {
          recentre(coordinate[0], coordinate[1], undefined, panelExpanded);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiclePositions, selectedItem, dragged, geolocated]);
  const recentre = (lng, lat, zoom, panelOpen = panelExpanded) => {
    const coordinate = normalizeCoordinate([lng, lat], {
      rejectZeroPair: true
    });
    if (!map.current || !coordinate) return false;
    const padding = getMapPadding(getLayoutState(true), panelOpen);
    const nextZoom = Number.isFinite(zoom) && zoom > 0 ? zoom : map.current.getZoom() < 14.5 ? 15.5 : map.current.getZoom();
    cameraControllerRef.current?.request({
      center: coordinate,
      padding,
      zoom: nextZoom,
      duration: 350,
      essential: true
    });
    setDragged(false);
    setIsUserCentered(true);
    return true;
  };
  const recentreFromUrl = (...args) => {
    const coordinate = normalizeCoordinate(args);
    return initialMapSelection.coordinate && coordinate?.[0] === initialMapSelection.coordinate[0] && coordinate?.[1] === initialMapSelection.coordinate[1] ? true : recentre(...args);
  };
  const prevPanelExpandedRef = useRef(panelExpanded);
  const prevPanelWidthRef = useRef(panelWidth);
  const prevViewportRef = useRef(viewport);
  const prevOpenHeightRef = useRef(height);
  const prevClosedHeightRef = useRef(closeHeight);
  useEffect(() => {
    if (!map.current) return;
    const panelChanged = prevPanelExpandedRef.current !== panelExpanded;
    const panelWidthChanged = prevPanelWidthRef.current !== panelWidth;
    const viewportChanged = prevViewportRef.current.width !== viewport.width || prevViewportRef.current.height !== viewport.height;
    const openHeightChanged = prevOpenHeightRef.current !== height;
    const closedHeightChanged = prevClosedHeightRef.current !== closeHeight;
    const syncCamera = () => {
      const nextPadding = getMapPadding(getLayoutState(true), panelExpandedRef.current);
      const currentRecenterTarget = getLiveRecenterTarget();
      if (initialMapSelection.hasSelection && !currentRecenterTarget) return;
      if (panelChanged || viewportChanged || openHeightChanged || closedHeightChanged || isDesktopRef.current && panelWidthChanged) {
        const shouldRecentreTarget = currentRecenterTarget?.lng !== undefined && currentRecenterTarget?.lat !== undefined && (viewportChanged || openHeightChanged || closedHeightChanged || isDesktopRef.current || !draggedRef.current);
        if (shouldRecentreTarget) {
          recentre(currentRecenterTarget.lng, currentRecenterTarget.lat, undefined, panelExpandedRef.current);
        } else {
          cameraControllerRef.current?.request({
            center: map.current.getCenter(),
            padding: nextPadding,
            zoom: map.current.getZoom(),
            duration: 350
          });
        }
      }
    };
    const frameId = window.requestAnimationFrame(() => {
      syncCamera();
    });
    prevPanelExpandedRef.current = panelExpanded;
    prevPanelWidthRef.current = panelWidth;
    prevViewportRef.current = viewport;
    prevOpenHeightRef.current = height;
    prevClosedHeightRef.current = closeHeight;
    return () => {
      window.cancelAnimationFrame(frameId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, panelExpanded, panelWidth, viewport, height, closeHeight]);
  const preferredSelectedStopName = selectedItem?.type === "stop" ? getPreferredStopName(preferredStopNames, selectedItem?.id, selectedItem?.id) : null;
  const selectedStopType = stopItemDetails?.stop_type || selectedItem?.stopType;
  const selectedStopName = preferredSelectedStopName || stopItemDetails?.stop_name || selectedItem?.name;
  const selectedStopOriginalName = preferredSelectedStopName && selectedItem?.name && preferredSelectedStopName !== selectedItem.name ? selectedItem.name : null;
  useEffect(() => {
    if (selectedItem?.type !== "stop") {
      setRenameModalIsOpen(false);
      setPreferredStopNameTemp("");
      return;
    }
    setPreferredStopNameTemp(selectedStopName || selectedItem?.name || "");
  }, [selectedItem, selectedStopName]);
  const savePreferredStopName = useCallback(value => {
    if (!selectedItem?.id) return;
    const updatedPreferredStopNames = preferredStopNames.some(el => getStoredStopId(el) === selectedItem.id) ? preferredStopNames.map(el => getStoredStopId(el) === selectedItem.id ? withStoredStopId({
      ...el,
      name: value
    }, value && selectedItem.id) : el) : [...preferredStopNames, withStoredStopId({
      name: value
    }, selectedItem.id)];
    setPreferredStopNames(updatedPreferredStopNames.filter(el => el.name?.length));
    setRenameModalIsOpen(false);
  }, [preferredStopNames, selectedItem, setPreferredStopNames]);
  const recenterTarget = getRecenterTarget();
  const renderSheetToggle = () => <button aria-expanded={isOpen} aria-label={isOpen ? "Close map details" : "Open map details"} className="pointer-events-auto grid h-9 w-9 flex-shrink-0 place-items-center rounded-full outline-none ring-blue-500/50 transition-all duration-150 hover:bg-zinc-100 focus-visible:ring active:bg-zinc-100 dark:hover:bg-zinc-800 dark:active:bg-zinc-800" onClick={() => isOpen ? close() : open()}>
      <Chevron className={`h-6 w-6 transition-all ${isOpen ? "rotate-180" : "rotate-0"}`} />
    </button>;
  const renderRecentreButton = ({
    inline = false
  } = {}) => {
    if (!recenterTarget) return null;
    const baseClass = "z-20 flex h-9 flex-nowrap items-center rounded-full bg-blue-500 pl-2.5 pr-4 font-bold text-white shadow-md outline-none ring-1 ring-blue-500/50 transition-all duration-200 hover:bg-blue-600 focus-visible:ring focus-visible:ring-blue-500/50 active:bg-blue-600 dark:hover:bg-blue-600 dark:active:bg-blue-600";
    const shouldShowRecentre = dragged || !isUserCentered;
    const visible = inline ? shouldShowRecentre && !hideMobileMapOverlays : shouldShowRecentre;
    return <button className={`${baseClass} ${visible ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`} onClick={() => recentre(recenterTarget.lng, recenterTarget.lat)} disabled={!dragged && isUserCentered}>
        {recenterTarget.type === "vehicle" ? <VehicleIcon className="mr-2 size-5 shrink-0" /> : recenterTarget.type === "location" ? <LocationMono className="mr-1 h-4 w-4" /> : <StopMarkerOutline className="mr-1 h-4 w-4" />}
        <span>Recentre</span>
      </button>;
  };
  const renderStopPanelHeader = (mobile = false) => <div className="flex gap-x-2">
      <div className="flex w-full flex-col items-start">
        <h2 className="w-full text-pretty pt-1.5 text-2xl font-semibold leading-none">
          {selectedStopName}
        </h2>
        <div className="flex flex-col items-start py-1 text-sm opacity-60">
          {selectedStopOriginalName && <p>{selectedStopOriginalName}</p>}
          <p>
            {getStopTypeDetails(selectedStopType).name}{" "}
            {getStopCodeDisplay(stopItemDetails || selectedItem)}
          </p>
          {stopItemDetails?.stop_description && <p>{stopItemDetails.stop_description}</p>}
        </div>
      </div>
      <FavouriteButton stopId={selectedItem.id} favourites={favourites} setFavourites={setFavourites} />
      {mobile ? renderSheetToggle() : null}
    </div>;
  const renderStopPanelBody = (mobile = false) => <div className="pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <div className="mb-4 flex flex-nowrap justify-stretch gap-2 px-[20px] pt-1">
        <Link href={`/stop/${selectedItem?.id}?from=map`} className={`flex-1 rounded-full bg-white px-4 py-2 text-center text-sm font-semibold leading-tight text-blue-500 outline-none ring-1 ring-blue-500/50 transition-all hover:bg-zinc-100 focus-visible:ring focus-visible:ring-blue-500/50 dark:bg-black dark:text-blue-300 dark:ring-blue-300 dark:hover:bg-zinc-800`}>
          {stopItemDetails?.is_arrival_only ? "Stop details" : "Departures"}
        </Link>
        <button type="button" onClick={() => setRenameModalIsOpen(true)} className={`flex-1 rounded-full bg-white px-4 py-2 text-sm font-semibold leading-tight text-blue-500 outline-none ring-1 ring-blue-500/50 transition-all hover:bg-zinc-100 focus-visible:ring focus-visible:ring-blue-500/50 dark:bg-black dark:text-blue-300 dark:ring-blue-300 dark:hover:bg-zinc-800`}>
          Rename
        </button>
        <FavouriteButton stopId={selectedItem.id} favourites={favourites} setFavourites={setFavourites} variant="pill" />
      </div>
      {stopPanelIsLoading ? <div className="px-[20px] text-sm opacity-60">
          Loading stop details…
        </div> : <>
          <div className="px-[20px]">
            <div className="mb-2 text-sm opacity-60">
              {getStopTypeDetails(selectedStopType).servicesLabel}
            </div>
            <div className="mb-4 grid min-h-8 w-full grid-cols-[repeat(auto-fill,_minmax(24px,_1fr))] gap-2">
              {stopItemDetails?.routes.length > 0 ? stopItemDetails.routes.map((route, key) => <RouteBadge key={key} route_color={route?.route_color} route_text_color={route?.route_text_color} routeLabel={route?.route_short_name} route_type={route?.route_type} />) : <div className="col-span-full mb-1 flex items-center opacity-60">
                  No services at this stop
                </div>}
            </div>
          </div>

          {stopItemDetails?.is_arrival_only ? <p className="mb-4 px-[20px] text-lg opacity-80">
              This stop is for arrivals only.
            </p> : <div className="mb-4">
              <div className="px-[20px] text-sm opacity-60">
                Next 10 Departures
              </div>
              {Array.isArray(stopDepartures) && stopDepartures.length > 0 ? <div className="flex flex-col">
                  {stopDepartures.slice(0, 10).map((departure, index) => {
            const route = stopItemDetails?.routes?.find(item => item.route_id === departure.route_id);
            return <MapStopDepartureRow key={`${departure.trip_id}-${departure.scheduled_departure_time}-${index}`} departure={departure} route={route} stopId={selectedItem?.id} />;
          })}
                </div> : <div className="col-span-full mb-1 flex items-center px-[20px] pt-2">
                  No upcoming departures
                </div>}
              <div className="flex w-full px-[20px] pt-4">
                <Link href={`/stop/${selectedItem?.id}?from=map`} className={`w-full flex-1 rounded-full bg-white px-4 py-2 text-center font-semibold leading-tight text-blue-500 outline-none ring-1 ring-blue-500/50 transition-all hover:bg-zinc-100 focus-visible:ring focus-visible:ring-blue-500/50 dark:bg-black dark:text-blue-300 dark:ring-blue-300 dark:hover:bg-zinc-800 ${mobile && !isOpen ? "opacity-0" : "opacity-100"}`}>
                  All Departures at This Stop
                </Link>
              </div>
            </div>}
        </>}
    </div>;
  const renderVehiclePanelHeader = (mobile = false) => {
    const schoolBusSchool = itemDetails?.schoolName;
    return <div className="flex min-h-9 justify-between gap-x-2">
        {itemDetails ? <>
            <RouteBadge route_color={selectedVehicleRouteDetails?.route_color} route_text_color={selectedVehicleRouteDetails?.route_text_color} routeLabel={selectedVehicleRouteDetails?.route_short_name} route_type={selectedVehicleRouteDetails?.route_type} className="mr-1 size-9 shrink-0" />
            <div className="flex w-full flex-col pt-1.5">
              <h2 className="text-2xl font-semibold leading-none">
                {selectedVehicleTripDetails?.trip_headsign}
              </h2>
              {schoolBusSchool ? <div className="mt-1.5 text-base font-normal leading-tight opacity-60">
                  {schoolBusSchool}
                </div> : null}
            </div>
          </> : <div className="grid h-9 w-9 place-items-center">
            <Spinner className="h-4 w-4 animate-spin text-blue-500 dark:text-blue-300" />
          </div>}
        {mobile ? renderSheetToggle() : null}
      </div>;
  };
  const renderVehiclePanelBody = (mobile = false) => {
    const hasVehiclePrimaryDetails = Boolean(itemDetails);
    const selectedVehicleTripHref = getSelectedVehicleTripHref(selectedItem);
    const canShowOccupancy = shouldShowVehiclePanelOccupancy(selectedVehicleRouteDetails?.route_type);
    const occupancyDetails = canShowOccupancy ? getOccupancyDetails(selectedItem?.occupancyStatus) : null;
    const shouldRenderOccupancyBlock = hasVehiclePrimaryDetails && canShowOccupancy && occupancyDetails;
    return <div className="px-[20px] pb-[calc(2rem+env(safe-area-inset-bottom))]">
        <div className="mb-6">
          <div className={`flex min-h-7 transition-opacity duration-150 ${hasVehiclePrimaryDetails ? "opacity-100" : "opacity-0"}`}>
            {selectedVehicleRouteDetails?.route_type ? transportTranslator(selectedVehicleRouteDetails?.route_type, selectedItem.id, busVehicleDetails?.vehicle) : ""}
          </div>

          {shouldRenderOccupancyBlock ? <div className="flex min-h-7 items-center text-pretty transition-opacity duration-150">
              {occupancyDetails ? <>
                  <div className="mr-3 flex w-9 flex-shrink-0 justify-center">
                    <occupancyDetails.IconComponent className={`h-4 w-4 ${occupancyDetails.colorClass}`} />
                  </div>
                  <div className="leading-tight">
                    {occupancyDetails.description}
                  </div>
                </> : null}
            </div> : null}
          {selectedItem?.type === "vehicle" ? <div className="flex min-h-7 items-center text-pretty">
              <div className="mr-3 grid min-h-7 w-9 flex-shrink-0 place-items-center">
                {selectedVehicleUsesLocationFallback ? <LocationMono className="size-4" /> : <StopMarker className={`h-[1.125rem] w-3 ${getStopMarkerClassName(selectedVehicleNextStopType)}`} />}
              </div>
              <div className="leading-tight">
                {selectedVehicleUsesLocationFallback ? `Near: ${selectedVehicleLocationName || "Loading nearby..."}` : `Next: ${selectedVehicleNextStopName || "Loading next..."}`}
              </div>
            </div> : null}
        </div>
        <div className="flex justify-between">
          {selectedVehicleTripHref ? <Link href={selectedVehicleTripHref} className={`w-full flex-1 rounded-full bg-white px-4 py-2 text-center font-semibold leading-tight text-blue-500 outline-none ring-1 ring-blue-500/50 transition-all duration-150 hover:bg-zinc-100 focus-visible:ring focus-visible:ring-blue-500/50 dark:bg-black dark:text-blue-300 dark:ring-blue-300 dark:hover:bg-zinc-800 ${mobile && !isOpen ? "opacity-0" : hasVehiclePrimaryDetails ? "opacity-100" : "opacity-0"}`}>
              See Live Trip
            </Link> : null}
        </div>
      </div>;
  };
  const renderScooterPanelHeader = (mobile = false) => {
    const HeaderIcon = selectedItem?.formFactor === "bicycle" ? Ebike : Escooter;
    const accentColor = getMicromobilityAccentColor(selectedItem?.markerImageId);
    return <div className="flex min-h-9 justify-between gap-x-2">
        <div className="grid size-9 shrink-0 place-items-center rounded-full text-white" style={{
        backgroundColor: accentColor
      }}>
          <HeaderIcon className="size-5" />
        </div>
        <div className="flex w-full flex-col pt-1.5">
          <h2 className="text-2xl font-semibold leading-none">
            {getMicromobilityTitle(selectedItem)}
          </h2>
        </div>
        {mobile ? renderSheetToggle() : null}
      </div>;
  };
  const renderScooterPanelBody = (mobile = false) => {
    const rangeMeters = Number(selectedItem?.rangeMeters);
    const lastReportedLabel = formatUpdatedLabel(selectedItem?.lastReported);
    const providerName = selectedItem?.provider || "provider";
    const vehicleCount = Number(selectedItem?.count) || 1;
    const isGroupedMicromobility = vehicleCount > 1;
    const stationName = typeof selectedItem?.stationName === "string" ? selectedItem.stationName.trim() : "";
    const hasStationGroup = isGroupedMicromobility && stationName;
    return <div className="px-[20px] pb-[calc(2rem+env(safe-area-inset-bottom))]">
        <div className="mb-6">
          {isGroupedMicromobility ? <div className="flex min-h-7 items-center text-pretty">
              <div className="mr-3 grid min-h-7 w-9 shrink-0 place-items-center">
                <Nearby className="size-4" />
              </div>
              <div className="leading-tight">
                {vehicleCount} {getMicromobilityPluralNoun(selectedItem)}
                {stationName ? ` at ${formatMicromobilityStationName(stationName)}` : ` within ${micromobilityGroupDisplayRadiusMeters}m of here`}
              </div>
            </div> : null}
          {!isGroupedMicromobility && Number.isFinite(rangeMeters) ? <div className="flex min-h-7 items-center text-pretty">
              <div className="mr-3 grid min-h-7 w-9 shrink-0 place-items-center">
                <Battery className="size-4" />
              </div>
              <div className="leading-tight">
                Range: {formatDistance(rangeMeters)}
              </div>
            </div> : null}
          {!hasStationGroup ? <div className="flex min-h-7 items-center text-pretty">
              <div className="mr-3 grid min-h-7 w-9 shrink-0 place-items-center">
                <LocationMono className="size-4" />
              </div>
              <div className="leading-tight">
                Near: {selectedMicromobilityLocationName || "Loading nearby..."}
              </div>
            </div> : null}
          {lastReportedLabel ? <div className="flex min-h-7 items-center text-pretty">
              <div className="mr-3 grid min-h-7 w-9 shrink-0 place-items-center">
                <Clock className="size-4" />
              </div>
              <div className="leading-tight">{lastReportedLabel}</div>
            </div> : null}
        </div>
        {selectedItem?.rentalUri ? <a href={selectedItem.rentalUri} className={`mb-4 block w-full rounded-full bg-white px-4 py-2 text-center font-semibold leading-tight text-blue-500 outline-none ring-1 ring-blue-500/50 transition-all duration-150 hover:bg-zinc-100 focus-visible:ring focus-visible:ring-blue-500/50 dark:bg-black dark:text-blue-300 dark:ring-blue-300 dark:hover:bg-zinc-800 ${mobile && !isOpen ? "opacity-0" : "opacity-100"}`}>
            Open in {providerName}
          </a> : null}
      </div>;
  };
  const renderEmptyPanelHeader = (mobile = false) => <div className="flex justify-between">
      <h1 className="w-full pr-2 pt-1.5 text-2xl font-semibold leading-none">
        {hasRecentStops ? "Recent stops" : "No stop or vehicle selected"}
      </h1>
      {mobile ? renderSheetToggle() : null}
    </div>;
  const renderEmptyPanelBody = (mobile = false) => hasRecentStops ? <Recents className={`pb-12 ${mobile && !isOpen ? "opacity-0" : "opacity-100"}`} emptyMessage="Choose a stop or vehicle from the map to see more details" isClient={isMounted} isLoading={recentStopsIsLoading} onStopSelect={async recentStop => {
    try {
      const response = await fetch(`/api/stops/${encodeURIComponent(recentStop.stop_id)}`);
      if (!response.ok) {
        throw new Error("Failed to load recent stop details");
      }
      const stopDetails = await response.json();
      handleStopSelect(stopDetails, {
        history: "push"
      });
    } catch (error) {
      console.error("Failed to reopen recent stop on map", {
        stopId: recentStop.stop_id,
        error
      });
    }
  }} preferredStopNames={preferredStopNames} showHeading={false} showWhenEmpty={true} stops={recentStops} type="all" /> : <p className={`px-[20px] pb-12 ${mobile && !isOpen ? "opacity-0" : "opacity-100"}`}>
        Choose a stop or vehicle from the map to see more details
      </p>;
  const renderLocationPanelHeader = (mobile = false) => <div className="flex gap-x-2">
      <div className="w-full">
        <h2 className="w-full text-pretty pt-1.5 text-2xl font-semibold leading-none">
          {searchLocation?.name || "Selected location"}
        </h2>
      </div>
      {mobile ? renderSheetToggle() : null}
    </div>;
  const renderLocationPanelBody = () => <div className="min-h-[500px] pb-[calc(2rem+env(safe-area-inset-bottom))] pt-2">
      <div className="px-[20px] text-sm opacity-60">Nearby stops</div>
      {nearbyStopsIsLoading ? <div className="px-[20px] pt-3 text-sm opacity-60">Loading …</div> : nearbyStops?.length > 0 ? nearbyStops.map(stop => <StopRow distance={formatDistance(stop.distance)} isFavourite={hasFavouriteStopId(favourites, stop.stop_id)} key={stop.stop_id} onStopSelect={() => handleNearbyStopSelect(stop)} stop_id={stop.stop_id} stop_name={stop.stop_name} stop_type={stop.stop_type} type="all" />) : <div className="px-[20px] py-2">
          No nearby stops found for this location.
        </div>}
    </div>;
  const renderPanelSections = (mobile = false) => {
    if (selectedItem?.type === "stop") {
      return {
        header: renderStopPanelHeader(mobile),
        body: renderStopPanelBody(mobile)
      };
    }
    if (selectedItem?.type === "vehicle" && showVehicles) {
      return {
        header: renderVehiclePanelHeader(mobile),
        body: renderVehiclePanelBody(mobile)
      };
    }
    if (selectedItem?.type === "scooter" && showScooters) {
      return {
        header: renderScooterPanelHeader(mobile),
        body: renderScooterPanelBody(mobile)
      };
    }
    if (searchLocation && panelMode !== "search") {
      return {
        header: renderLocationPanelHeader(mobile),
        body: renderLocationPanelBody()
      };
    }
    return {
      header: renderEmptyPanelHeader(mobile),
      body: renderEmptyPanelBody(mobile)
    };
  };
  const renderDesktopSidebarBody = () => {
    const {
      header,
      body
    } = renderPanelSections();
    return <div className="pointer-events-auto relative z-20 flex h-full w-full flex-col overflow-hidden">
        <div className={`w-full px-[20px] pt-3 ${panelMode === "search" ? "min-h-0 flex-1" : ""}`}>
          <SearchGeoCode variant="sidebar" className={panelMode === "search" ? "h-full w-full" : "w-full"} onActiveChange={active => setPanelMode(currentMode => active ? "search" : currentMode === "location" ? "location" : "selection")} onSelectLocation={handleLocationSelect} onSelectStop={handleStopSelect} lastMapCenter={lastMapCenter} preferredStopNames={preferredStopNames} />
        </div>
        {panelMode !== "search" ? <>
            <div className={`shrink-0 px-[20px] pb-2 pt-4 transition-shadow ${panelScrolled ? "shadow-lg" : ""}`}>
              {header}
            </div>
            <div ref={desktopPanelBodyRef} className="min-h-0 flex-1 overflow-y-auto overscroll-none">
              {body}
            </div>
          </> : null}
      </div>;
  };
  const micromobilityIsHiddenByZoom = showScooters && mapZoom < micromobilityMinVisibleZoom;
  const micromobilityToggleStateClass = showScooters ? micromobilityIsHiddenByZoom ? "font-bold text-pink-500 opacity-60 saturate-50 dark:text-pink-400" : "font-bold text-pink-500 dark:text-pink-400" : "text-inherit";
  const micromobilityToggleTitle = scooterPositionsError ? "Shared bikes and scooters unavailable" : micromobilityIsHiddenByZoom ? "Bikes and scooters hidden until you zoom in" : showScooters ? "Hide shared bikes and scooters" : mapZoom < micromobilityMinVisibleZoom ? "Show bikes and scooters when zoomed in" : "Show shared bikes and scooters";
  return <>
      <Head>
        <title key="title">{mapPageMetadata.title}</title>
        <meta name="twitter:text:title" content={mapPageMetadata.title} key="twitterTitle" />
        <meta name="twitter:text:description" content={mapPageMetadata.description} key="twitterDescription" />
        <meta property="og:title" content={mapPageMetadata.title} key="ogTitle" />
        <meta property="og:description" content={mapPageMetadata.description} key="ogDescription" />
        <meta name="description" content={mapPageMetadata.description} key="description" />
        <meta property="og:image" content={`${canonicalSiteUrl}/share/default-share-image.png`} key="ogImage" />
        <meta property="og:url" content={`${canonicalSiteUrl}/map`} key="ogUrl" />
        <link rel="canonical" href={`${canonicalSiteUrl}/map`} key="canonicalUrl" />
      </Head>

      <Modal isOpen={renameModalIsOpen} afterEnter={() => {
      window.setTimeout(() => {
        renameInputRef.current?.focus();
      }, 150);
    }} onClose={() => setRenameModalIsOpen(false)} title={`Rename ${selectedStopType === "bus" ? "Stop" : "Station"}`}>
        <form onSubmit={e => {
        e.preventDefault();
        savePreferredStopName(preferredStopNameTemp.trim());
      }}>
          <div className="relative mt-3 px-[20px]">
            <div className="flex rounded ring-1 ring-zinc-500/50 duration-100 focus-within:ring focus-within:ring-blue-500/50">
              <input type="text" className={`transition-colors h-12 w-full appearance-none border-none bg-transparent py-2 pl-3 text-lg placeholder-zinc-400 outline-none ${preferredStopNameTemp?.length > 0 ? "pr-12" : "pr-3"}`} placeholder={selectedItem?.name || ""} value={preferredStopNameTemp ? preferredStopNameTemp : ""} onChange={e => setPreferredStopNameTemp(e.target.value)} ref={renameInputRef} spellCheck="false" autoCapitalize="true" autoComplete="false" autoCorrect="false" enterKeyHint="done" />
              {preferredStopNameTemp?.length > 0 && <button onClick={() => {
              renameInputRef.current.focus();
              setPreferredStopNameTemp("");
            }} className="pointer-events-auto absolute right-[calc(20px+0.5rem)] top-1.5 z-10 grid h-9 w-9 flex-shrink-0 place-items-center rounded-full outline-none ring-blue-500/50 transition-all duration-150 hover:bg-zinc-400/10 focus-visible:ring active:bg-zinc-400/10" type="button">
                  <CloseCross role="display" title="Close" className="size-[1.125rem] text-zinc-500 dark:text-zinc-300" />
                </button>}
            </div>
          </div>

          <div className="flex flex-row-reverse gap-2 p-3 font-semibold text-blue-500 dark:text-white">
            <DialogAction type="submit">
              Save
            </DialogAction>
            <DialogAction onClick={() => setRenameModalIsOpen(false)} type="button">
              Cancel
            </DialogAction>
          </div>
        </form>
      </Modal>

      <div ref={mobileChromeRef} className={`pointer-events-none sticky top-0 touch-none pt-4 ${mobileSearchActive ? "z-40" : "z-20"}`}>
        <div className="mb-2 w-full px-[20px]">
          {!isDesktop && <BackLink onMap={true} onBack={handleMapBack} />}
        </div>
        <div className="pointer-events-none flex touch-none flex-col items-end">
          {!isDesktop && <div className="pointer-events-auto w-full">
              <SearchGeoCode className="w-full" onActiveChange={setMobileSearchActive} onSelectLocation={handleLocationSelect} onSelectStop={handleStopSelect} lastMapCenter={lastMapCenter} preferredStopNames={preferredStopNames} />
            </div>}

          {isMounted && !isDesktop && <div className={`flex flex-col flex-nowrap items-end justify-between gap-y-2 pb-4 pr-[20px] pt-2 transition-opacity duration-200 ${hideMobileMapOverlays ? "pointer-events-none opacity-0" : "opacity-100"}`}>
              {mapStyleMenu}
              <IconButton label={showVehicles ? "Hide live vehicles" : "Show live vehicles"} aria-pressed={showVehicles} variant="map" className={showVehicles ? "font-bold text-blue-500 dark:text-blue-400" : "text-inherit"} onClick={handleVehicleVisibilityToggle}>
                {vehiclePositionsIsLoading ? <Spinner aria-hidden="true" className="h-4 w-4 animate-spin text-blue-500 dark:text-blue-300" /> : <Bus aria-hidden="true" className="relative top-px size-[1.125rem]" />}
              </IconButton>
              <IconButton label={micromobilityToggleTitle} aria-pressed={showScooters} variant="map" className={micromobilityToggleStateClass} onClick={handleScooterVisibilityToggle}>
                {scooterPositionsIsLoading ? <Spinner aria-hidden="true" className="h-4 w-4 animate-spin text-pink-500 dark:text-pink-400" /> : <Micromobility aria-hidden="true" className="size-[1.125rem]" />}
              </IconButton>

              {canShowGeolocationControls(geolocatePermissionState) ? <IconButton label={geolocatePermissionState === "denied" ? "Location unavailable" : "Use my location"} variant="map" className={`relative transition-opacity duration-200 ${geolocated && !dragged && !isUserCentered ? "text-blue-500 dark:text-blue-400" : "text-zinc-900 dark:text-white"}`} onClick={handleGeolocateClick}>
                  <Spinner aria-hidden="true" className={`absolute size-4 animate-spin text-blue-500 transition-opacity dark:text-blue-300 ${geolocating ? "opacity-100" : "opacity-0"}`} />
                  <MyLocation aria-hidden="true" className={`absolute size-4 transition-opacity ${geolocating ? "opacity-0" : "opacity-100"}`} />
                </IconButton> : null}

              <IconButton label="Reset map bearing" variant="map" className={`z-20 text-zinc-400 transition-opacity ${rotated ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`} onClick={() => {
            map.current?.easeTo({
              bearing: 0,
              around: map.current.getCenter()
            });
            setRotated(false);
          }} disabled={!rotated}>
                <Compass aria-hidden="true" className="relative h-7 w-7" style={{
              transform: `rotate(${-1 * rotation}deg)`
            }} />
              </IconButton>
            </div>}
        </div>
      </div>

      <div ref={mobileMapRef} className="absolute inset-0 h-screen w-screen">
        <div ref={mapContainerRef} className="relative h-screen w-screen" />
      </div>
      {vehiclePositionsError && <div role="status" aria-live="polite" className="fixed inset-x-4 top-16 z-30 mx-auto flex max-w-sm items-center justify-between gap-3 rounded-lg bg-white px-4 py-3 text-sm shadow-lg dark:bg-black">
          <span>
            {vehiclePositions.features.length > 0 ? "Live vehicle positions may be out of date." : "Live vehicle positions are unavailable."}
          </span>
          <button type="button" onClick={() => void refreshVehiclePositions()} className="font-semibold text-blue-600 dark:text-blue-400">
            Retry
          </button>
        </div>}
      {/* debug */}
      {/* <div className="relative z-50 mt-20 block bg-white/50 p-4 backdrop-blur-sm">
        {dragged ? "dragged" : "not dragged"}
        <br />
        {isUserCentered ? "user centered" : "not user centered"}
        <br />
        {geolocating ? "geolocating" : "not geolocating"}
        <br />
        {geolocated ? "geolocated" : "not geolocated"}
        <br />
        {selectedFeatureId && selectedFeatureId}
        <br />
        {selectedItem && selectedItem.stopType}
       </div> */}

      {isDesktop && isMounted && <>
          <div className="pointer-events-none fixed inset-x-0 top-0 z-20 flex justify-between px-5 pb-2 pt-4">
            <BackLink onMap={true} onBack={handleMapBack} />
            <div className="flex flex-col gap-y-2">
              {mapStyleMenu}
              <IconButton label={showVehicles ? "Hide live vehicles" : "Show live vehicles"} aria-pressed={showVehicles} variant="map" className={showVehicles ? "font-bold text-blue-500 dark:text-blue-400" : "text-inherit"} onClick={handleVehicleVisibilityToggle}>
                {vehiclePositionsIsLoading ? <Spinner aria-hidden="true" className="h-4 w-4 animate-spin text-blue-500 dark:text-blue-300" /> : <Bus aria-hidden="true" className="relative top-px size-[1.125rem]" />}
              </IconButton>
              <IconButton label={micromobilityToggleTitle} aria-pressed={showScooters} variant="map" className={micromobilityToggleStateClass} onClick={handleScooterVisibilityToggle}>
                {scooterPositionsIsLoading ? <Spinner aria-hidden="true" className="h-4 w-4 animate-spin text-pink-500 dark:text-pink-400" /> : <Micromobility aria-hidden="true" className="size-[1.125rem]" />}
              </IconButton>
              {canShowGeolocationControls(geolocatePermissionState) ? <IconButton label={geolocatePermissionState === "denied" ? "Location unavailable" : "Use my location"} variant="map" className={`relative transition-opacity duration-200 ${geolocated && !dragged && !isUserCentered ? "text-blue-500 dark:text-blue-400" : "text-zinc-900 dark:text-white"}`} onClick={handleGeolocateClick}>
                  <Spinner aria-hidden="true" className={`absolute size-4 animate-spin text-blue-500 transition-opacity dark:text-blue-300 ${geolocating ? "opacity-100" : "opacity-0"}`} />
                  <MyLocation aria-hidden="true" className={`absolute size-4 transition-opacity ${geolocating ? "opacity-0" : "opacity-100"}`} />
                </IconButton> : null}
              <IconButton label="Reset map bearing" variant="map" className={`z-20 text-zinc-400 transition-opacity ${rotated ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`} onClick={() => {
            map.current?.easeTo({
              bearing: 0,
              around: map.current.getCenter()
            });
            setRotated(false);
          }} disabled={!rotated}>
                <Compass aria-hidden="true" className="relative h-7 w-7" style={{
              transform: `rotate(${-1 * rotation}deg)`
            }} />
              </IconButton>
            </div>
          </div>
          <div className="pointer-events-none fixed inset-y-0 left-0 z-10">
            <div className="flex h-full flex-col justify-end px-4 pb-4">
              <div ref={panelRef} className="pointer-events-auto flex h-[calc(100vh-5rem)] w-[min(calc(40vw),26rem)] flex-col overflow-hidden rounded-2xl bg-white/80 bg-blend-color-dodge shadow-lg backdrop-blur dark:bg-black/80">
                {renderDesktopSidebarBody()}
              </div>
            </div>
          </div>
          <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 flex justify-end px-5 pb-4">
            {renderRecentreButton()}
          </div>
        </>}

      {!isDesktop && <>
          <a.div aria-hidden="true" className={`fixed inset-0 z-20 bg-zinc-950/30 dark:bg-zinc-950/70 ${isFullOpen ? "pointer-events-auto" : "pointer-events-none"}`} style={{
        opacity: y.to(value => allowDrawerFullSnap ? getDrawerBackdropProgress(value, heightRef.current, fullHeightRef.current) : 0)
      }} />
          <a.div ref={mobileInlineRecentreRef} className="pointer-events-none fixed inset-x-0 mx-auto w-full max-w-xl px-4" style={{
        bottom: y.to(value => `${value + 16}px`)
      }}>
            <div className="flex justify-end">
              {renderRecentreButton({
            inline: true
          })}
            </div>
          </a.div>
          <a.div className="pointer-events-auto fixed inset-x-0 bottom-0 z-30 mx-auto w-full max-w-xl touch-none select-none" style={{
        height: y
      }}>
            <div className={`h-full rounded-t-2xl bg-white/80 bg-blend-color-dodge backdrop-blur dark:bg-black/80`}>
              <div className="pointer-events-auto relative z-20 flex h-full flex-col shadow-lg">
                <div ref={mobileSheetHeaderRef} {...headerBind()} className={`z-10 shrink-0 touch-none px-[20px] py-4 transition-shadow ${panelScrolled ? "shadow-lg" : ""}`}>
                  <svg viewBox="0 0 30 4" xmlns="http://www.w3.org/2000/svg" width="30" height="4" className="mx-auto mb-1.5 opacity-30">
                    <rect x="0" width="30" height="4" rx="2" fill="currentColor" />
                  </svg>
                  {renderPanelSections(true).header}
                </div>
                <div ref={mobileSheetBodyRef} {...bodyBind()} className={`box-border min-h-0 flex-1 overscroll-none [scrollbar-gutter:stable] ${isOpen ? "overflow-y-auto" : "overflow-y-hidden"}`} style={{
              touchAction: "pan-y"
            }}>
                  <div ref={mobileSheetBodyContentRef}>
                    {renderPanelSections(true).body}
                  </div>
                </div>
              </div>
            </div>
          </a.div>
        </>}
    </>;
};
export default Map;